/**
 * VoiceWidgetConnected - VoiceWidget с подключенной логикой LiveKit
 *
 * Этот компонент связывает чистый UI (VoiceWidget) с LiveKit логикой.
 * Вся логика взята из LiveKitAssistant.jsx и адаптирована для работы с VoiceWidget.
 *
 * Использование:
 * <VoiceWidgetConnected
 *   theme={themes.shiftgears}
 *   onClose={() => closeWidget()}
 * />
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  subscribeFormModal,
  isFormModalOpen,
} from '../../utils/formModalSignal';
import { Track } from 'livekit-client';
import { usePortalRoot } from '../../context/PortalContext';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  StartAudio,
  useRoomContext,
  useTrackToggle,
  useVoiceAssistant as useLiveKitVoiceAssistant,
  useRemoteParticipants,
  useChat,
} from '@livekit/components-react';
import '@livekit/components-styles';

import { VoiceWidget, themes, getThemeStyles } from './index';
import { useVoiceAssistant as useVoiceAssistantContext } from '../../context/VoiceAssistantContext';
import useLiveKitConnection from '../../hooks/useLiveKitConnection';
import useLiveKitTranscript from '../../hooks/useLiveKitTranscript';
import useTextChat from '../../hooks/useTextChat';
import { useDraggable } from '../../hooks/useDraggable';
import { useResizable } from '../../hooks/useResizable';
import { useContactInfo } from '../../hooks/useContactInfo';
import {
  getOrCreateSessionId,
  refreshSession,
  clearSession,
} from '../../services/sessionManager';
import { browserControlWS } from '../../services/browserControlWebSocket';
import { commandExecutor } from '../../services/commandExecutor';
import { getCurrentDateTime } from '../../utils/dateTime';
import { telegramUrl } from '../../utils/telegram';
import { saveMessages, loadMessages } from '../../services/messageHistory';
import {
  appendToStoredMessages,
  keepInFlight,
  proactiveMessageId,
  queueEntry,
  restoredQueueEntries,
} from './handoverMessages';
import {
  loadWidgetGeometry,
  saveWidgetGeometry,
  getDefaultGeometry,
  ensurePositionInViewport,
} from '../../services/widgetGeometry';
import { config } from '../../config/runtimeConfig';
import {
  setMessagesRef,
  setSendMessageFn,
  setSessionId as setTestingSessionId,
} from '../../services/testingApi';
import {
  initAnalytics,
  setAnalyticsSessionId,
  trackWidgetOpen,
  trackWidgetClose,
  trackModeSwitch,
  trackConversationStart,
  trackError,
} from '../../services/analyticsBridge';
import { setBeaconSessionId } from '../../services/analyticsBeacon';
import { chatMirror } from '../../services/chatMirror';
import { ConsentPopover } from './ConsentPopover';
import { mergeCheckpointMessages } from './checkpointHistory';
import { t as widgetT } from './translations';
import { useTranslation } from '../../i18n/I18nContext';
import './VoiceWidgetConnected.css';

/**
 * Загрузить сообщения из localStorage и замапить роли
 * @param {string} sessionId - ID сессии
 * @returns {Array} Массив сообщений с замапленными ролями
 */
const loadAndMapMessages = (sessionId) => {
  const loadedMessages = loadMessages(sessionId);
  return loadedMessages.length > 0
    ? loadedMessages.map((msg) => ({
        ...msg,
        role: msg.role === 'ai' ? 'assistant' : msg.role,
      }))
    : [];
};

// Module-level cache для сохранения вычисленного initial mode
// Нужен потому что React StrictMode ремонтирует компоненты, и useRef сбрасывается
// При первом mount вычисляем режим и кэшируем, при последующих используем кэш
// Cache сбрасывается через таймаут чтобы работать только для текущей загрузки страницы
let cachedInitialMode = null;
let cachedIsNavigationRestore = false; // Issue #566: true when restoring after go_website_page navigation
let cachedRestoreData = null; // Issue #581: full restoreData for page_navigated launch_context
let cacheTimestamp = 0;
const CACHE_TTL_MS = 5000; // Cache живёт 5 секунд - достаточно для React StrictMode remounts

// WS_URL is now a function to ensure it reads the latest runtimeConfig value
// because window.runtimeConfig is set AFTER the module is loaded (in widget.jsx mount())
const getWsUrl = () => {
  const wsUrl = config.BROWSER_CONTROL_WS_URL;
  console.warn('[VoiceWidgetConnected] getWsUrl():', wsUrl);
  return wsUrl;
};

/**
 * Helper function to send page context with launch context
 * Issue #448: Extracted to avoid code duplication (DRY)
 * @param {Object} pageContext - Current page context
 * @param {Object|null} launchContext - Launch context (one-time use)
 * @param {Function} clearLaunchContext - Callback to clear launch context after sending
 */
const sendPageContextWithLaunch = (
  pageContext,
  launchContext,
  clearLaunchContext
) => {
  const contextWithLaunch = launchContext
    ? { ...pageContext, launchContext }
    : pageContext;
  browserControlWS.sendPageContext(contextWithLaunch);
  // Clear launchContext after sending (одноразовый контекст)
  if (launchContext) {
    clearLaunchContext();
  }
};

/**
 * Issue #1353: one place that answers "what mode does this dealer actually get?".
 *
 * A text-only dealer (`features.disableVoiceMode`) has no voice at all, so every
 * request for it — a restore marker left over from before the flag was flipped,
 * `?sg-restore=voice`, an `initialMode` prop, an SDK `open('voice')` — resolves
 * to the text chat instead of silently doing nothing. Exported so the rule is
 * unit-testable without mounting the widget (it owns LiveKit + WS).
 *
 * @param {string|undefined} requestedMode — 'collapsed' | 'chat' | 'voice' | 'text'
 * @param {boolean} voiceEnabled
 * @returns {string|undefined} the requested mode, or 'chat' when voice was asked
 *   for but the dealer has none.
 */
export const degradeVoiceMode = (requestedMode, voiceEnabled) =>
  requestedMode === 'voice' && !voiceEnabled ? 'chat' : requestedMode;

const generateMessageId = (prefix = '') => {
  const random = Math.random().toString(36).substring(2, 9);
  return prefix
    ? `${prefix}-${Date.now()}-${random}`
    : `${Date.now()}-${random}`;
};

/**
 * VoiceWidgetConnected - VoiceWidget with LiveKit connection
 *
 * @param {Object} props
 * @param {Object} [props.theme] - Theme object from themes.js (e.g., themes.shiftgears, themes.shiftgearsDark)
 * @param {Function} [props.onClose] - Called when widget is closed
 * @param {string} [props.title] - Widget title (default: "Janette")
 * @param {boolean} [props.showWhatsApp] - Show WhatsApp button
 * @param {string} [props.whatsAppNumber] - WhatsApp number
 * @param {boolean} [props.showTelegram] - Show Telegram button (#1493)
 * @param {string} [props.telegramHandle] - Telegram handle
 * @param {number} [props.width] - Widget width
 * @param {number} [props.height] - Widget height
 *
 * @example
 * // Light theme (default)
 * <VoiceWidgetConnected theme={themes.shiftgears} />
 *
 * // Dark theme
 * <VoiceWidgetConnected theme={themes.shiftgearsDark} />
 *
 * // Custom theme
 * <VoiceWidgetConnected theme={{ primaryColor: '#2563eb', accentColor: '#1e3a5f', variant: 'light' }} />
 */
import { DEFAULT_TERMS_URL, DEFAULT_PRIVACY_URL } from './legalDefaults';
export const VoiceWidgetConnected = ({
  theme = themes.shiftgears,
  // onClose prop is ignored - close always goes to collapsed mode
  onClose: _onClose,
  title,
  showWhatsApp,
  whatsAppNumber,
  showTelegram,
  telegramHandle,
  width = 400,
  height = 500,
  initialMode = 'collapsed', // 'collapsed' | 'voice' | 'chat'
  termsUrl = DEFAULT_TERMS_URL,
  privacyUrl = DEFAULT_PRIVACY_URL,
  /**
   * Встроить чат в поток страницы вместо плавающего окна (#1330, пункт 7).
   *
   * ⚠️ Только для витрины. По умолчанию `false`, и при `false` поведение
   * побайтово прежнее: все ветки ниже начинаются с проверки этого флага и
   * при выключенном возвращают то же, что возвращали.
   *
   * Инлайн-режим отключает позиционирование `fixed`, перетаскивание, ресайз
   * и сворачивание — в потоке страницы всё это либо бессмысленно, либо
   * оставляет дыру на месте свёрнутого блока.
   */
  inline = false,
  /**
   * DOM-узел, в который портировать чат вместо `document.body`. Задаётся
   * вместе с `inline`; без него инлайн-режим смысла не имеет.
   */
  inlineHost = null,
  /**
   * Отражать переписку в общем зеркале (`services/chatMirror`) — #1330.
   *
   * Нужно витрине: она показывает рядом мокап инбокса дилера, и переписка
   * должна появляться там же, как появилась бы у команды — включая ответы
   * ассистента. По умолчанию выключено: дилерам это не нужно.
   *
   * Зеркало — внешний стор, а не колбэк наверх, и это важно: ответ приходит
   * ПОТОКОМ, и прокидывание каждого куска через React перерисовывало бы весь
   * виджет на каждый токен. Подписан только мокап.
   */
  mirror = false,
  /** #1417: язык подписей виджета. Передаёт только витрина (`App.jsx`). */
  language = 'en',
  // #1353 — text-only dealer. Undefined → resolved from the widget config
  // (`features.disableVoiceMode`); an explicit prop wins, for tests/Storybook.
  voiceEnabled: voiceEnabledProp,
}) => {
  /**
   * Issue #1353: text-only dealers (Autodis) run the widget WITHOUT voice.
   * This is the single gate for every voice entry point:
   *   - the mic button in the collapsed bar / the voice-chat toggle in the
   *     chat header are not rendered (see VoiceWidget `voiceEnabled`);
   *   - `handleMicClick` opens the text chat instead of a LiveKit session;
   *   - `openChat({mode:'voice'})` (SDK `B2BDemo.open('voice')`, Sparkles,
   *     context shim) degrades to chat;
   *   - `handleModeChange('voice')` is a no-op;
   *   - a restore-into-voice (`sg_restore_widget`, `?sg-restore=voice`) lands
   *     in chat — the flag can be flipped while a visitor mid-navigation
   *     carries a stale voice restore marker.
   */
  const voiceEnabled = voiceEnabledProp ?? config.VOICE_ENABLED;
  // Widget mode: 'collapsed' | 'voice' | 'chat'
  // Map 'text' to 'chat' for compatibility with VoiceAssistantContext
  // Also checks for widget state restoration after go_website_page navigation
  //
  // ВАЖНО: Эта функция должна вызываться ТОЛЬКО ОДИН раз при инициализации!
  // Повторные вызовы не найдут флаг (он удаляется) и вернут 'collapsed'.
  // Используем useRef для сохранения первоначального режима.
  const computeInitialMode = () => {
    console.warn('[566-DEBUG] computeInitialMode() called');
    // TTL для флага восстановления - 60 секунд
    const RESTORE_TTL_MS = 60 * 1000;

    // 1. Проверяем localStorage на наличие флага восстановления (для того же домена)
    try {
      const restoreDataStr = localStorage.getItem('sg_restore_widget');
      console.warn(
        '[566-DEBUG] localStorage sg_restore_widget:',
        restoreDataStr
      );
      if (restoreDataStr) {
        const restoreData = JSON.parse(restoreDataStr);
        const age = Date.now() - (restoreData.timestamp || 0);
        console.warn(
          '[566-DEBUG] restoreData:',
          JSON.stringify(restoreData),
          ', age:',
          age,
          'ms'
        );

        // Проверяем TTL - флаг валиден только 60 секунд после навигации
        if (age < RESTORE_TTL_MS && restoreData.mode) {
          // Удаляем флаг после использования (одноразовый)
          localStorage.removeItem('sg_restore_widget');
          // Issue #566: Mark as navigation restore for auto-start logic
          cachedIsNavigationRestore = true;
          // Issue #581: Cache full restoreData for page_navigated launch_context
          cachedRestoreData = restoreData;
          console.log(
            `[566-DEBUG] computeInitialMode() => mode: '${restoreData.mode}', isNavigationRestore: true`
          );
          console.log(
            `[VoiceWidgetConnected] Restoring widget state after navigation:`,
            {
              mode: restoreData.mode,
              ageMs: age,
              targetPage: restoreData.targetPage,
            }
          );
          // #1353: a text-only dealer never restores INTO voice.
          return degradeVoiceMode(restoreData.mode, voiceEnabled);
        } else {
          // Флаг устарел - удаляем
          localStorage.removeItem('sg_restore_widget');
          console.log(
            `[VoiceWidgetConnected] Restore flag expired (age: ${age}ms), removed`
          );
        }
      }
    } catch (error) {
      console.warn(
        `[VoiceWidgetConnected] Error checking localStorage restore flag:`,
        error
      );
    }

    // 2. Fallback: проверяем query параметр sg-restore (для cross-domain навигации)
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const sgRestore = urlParams.get('sg-restore');
      if (sgRestore === 'chat' || sgRestore === 'voice') {
        // Issue #566: Mark as navigation restore for auto-start logic
        cachedIsNavigationRestore = true;
        console.log(
          `[VoiceWidgetConnected] Restoring widget state from query param: ${sgRestore}`
        );
        // Опционально: убираем параметр из URL для чистоты (без перезагрузки)
        urlParams.delete('sg-restore');
        const newUrl = urlParams.toString()
          ? `${window.location.pathname}?${urlParams.toString()}${
              window.location.hash
            }`
          : `${window.location.pathname}${window.location.hash}`;
        window.history.replaceState({}, '', newUrl);
        // #1353: `?sg-restore=voice` degrades to chat for text-only dealers.
        return degradeVoiceMode(sgRestore, voiceEnabled);
      }
    } catch (error) {
      console.warn(
        `[VoiceWidgetConnected] Error checking query param restore:`,
        error
      );
    }

    // 3. Стандартная логика определения начального режима
    const requestedMode =
      initialMode === 'text'
        ? 'chat'
        : initialMode === 'voice'
        ? 'voice'
        : initialMode || 'collapsed';
    // #1353: an `initialMode='voice'` from a caller degrades to chat.
    const defaultMode = degradeVoiceMode(requestedMode, voiceEnabled);
    console.log(
      `[566-DEBUG] computeInitialMode() => mode: '${defaultMode}', isNavigationRestore: false (default path)`
    );
    return defaultMode;
  };

  // Используем module-level cache для сохранения первоначального режима
  // (useRef не работает с React StrictMode - сбрасывается при remount)
  // Cache имеет TTL чтобы работать только в рамках текущей загрузки страницы
  const initialModeRef = useRef(null);
  const isNavigationRestoreRef = useRef(false); // Issue #566: true when restoring after navigation
  const now = Date.now();
  const cacheValid =
    cachedInitialMode !== null && now - cacheTimestamp < CACHE_TTL_MS;

  if (!cacheValid) {
    cachedIsNavigationRestore = false; // Reset before computation
    cachedInitialMode = computeInitialMode();
    cacheTimestamp = now;
    console.log(
      `[VoiceWidgetConnected] Computed and cached initial mode: ${cachedInitialMode}, isNavigationRestore: ${cachedIsNavigationRestore}`
    );
  } else {
    console.log(
      `[VoiceWidgetConnected] Using cached initial mode: ${cachedInitialMode} (age: ${
        now - cacheTimestamp
      }ms)`
    );
  }
  initialModeRef.current = cachedInitialMode;
  isNavigationRestoreRef.current = cachedIsNavigationRestore;

  const [mode, setMode] = useState(cachedInitialMode);
  const [previousMode, setPreviousMode] = useState(null);
  // Mini-collapsed: на мобилке стартуем в свёрнутом виде (только иконка + title)
  const [isMiniCollapsed, setIsMiniCollapsed] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  // #1232 item 3 — hide this floating widget while a form modal is open.
  const [formModalOpen, setFormModalOpen] = useState(isFormModalOpen);
  useEffect(() => subscribeFormModal(setFormModalOpen), []);
  const lastPageScrollTop = useRef(0);
  const [shouldConnect, setShouldConnect] = useState(false);
  const [messages, setMessages] = useState([]);
  // Изолированный звонок (#1441, карточка звонка на витрине): голос идёт своей
  // сессией агента, а переписка чата замораживается и после звонка
  // возвращается как была. State — для эффектов (зеркало, возврат), ref — для
  // колбэков подключения, которые не пересоздаются на каждую смену флага.
  const [isolatedCall, setIsolatedCall] = useState(false);
  const isolatedCallRef = useRef(false);
  const callSnapshotRef = useRef(null);
  // Поколение звонка: кладут трубку — счётчик растёт, и старт, который ещё ждёт
  // микрофона или токена, видит, что его отменили (ревью !1301: иначе звонок
  // поднимался уже ПОСЛЕ «положить трубку», и остановить его было нечем).
  const callGenRef = useRef(0);
  // Текст, набранный в чат во время звонка (очередь): сначала звонок
  // кончается и чат возвращается к снимку, потом текст уходит обычным путём.
  const sendAfterCallRef = useRef(null);
  // Поздние ссылки: обработчики объявлены ниже тех, кто их зовёт.
  const closeInlineVoiceRef = useRef(null);
  const handleSendMessageRef = useRef(null);
  // Последняя переписка для снимка на старте звонка: читать её из колбэка
  // через state нельзя — замыкание видело бы устаревший массив.
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  const [sessionState, setSessionState] = useState({
    status: 'disconnected',
    isSpeaking: false,
    isMuted: false,
    micPending: false,
  });
  const [controls, setControls] = useState({
    toggleMute: async () => {},
    endCall: async () => {},
  });
  const [connectionError, setConnectionError] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [roomKey, setRoomKey] = useState(0);
  const [speakerMuted, setSpeakerMuted] = useState(false);
  // Mobile detection: 768px breakpoint includes tablets (iPad mini 768px)
  // Standard mobile-first breakpoint matching CSS @media queries
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  );
  const [isVoiceInChat, setIsVoiceInChat] = useState(false);
  // Issue #752: Auto-suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const latestAssistantMsgIdRef = useRef(null);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [userAudioLevel, setUserAudioLevel] = useState(0);
  const [agentAudioLevel, setAgentAudioLevel] = useState(0);
  const [widgetState, setWidgetState] = useState('normal'); // 'normal' | 'maximized'
  const [isModeChanging, setIsModeChanging] = useState(false); // Индикатор переключения режима
  // Согласие с условиями — персистентное, читается из localStorage
  const [consentGiven, setConsentGiven] = useState(
    () => localStorage.getItem('widget_consentGiven') === 'true'
  );
  const [showConsentPopover, setShowConsentPopover] = useState(false);
  const pendingActionRef = useRef(null); // 'voice' | 'chat' — действие после подтверждения
  // Issue #496: Счётчик версии текстового чата для принудительной переинициализации useTextChat при смене режима
  // Используем счётчик вместо boolean чтобы useEffect перезапускался даже при повторном переключении в тот же режим
  const [textChatVersion, setTextChatVersion] = useState(0);

  // Get contact info from unified source (same as CarCard, ContactPage)
  const contactInfo = useContactInfo();

  // Get portal root for rendering widget
  // Inside Shadow DOM: returns portalRoot element (keeps CSS styles)
  // Standalone: returns document.body
  const portalTarget = usePortalRoot();

  // Скролл страницы вниз → сворачиваем collapsed bar в mini (только мобилка)
  const scrollRafRef = useRef(null);
  useEffect(() => {
    if (!isMobile || mode !== 'collapsed') return;

    const handlePageScroll = () => {
      // Одна обработка за кадр — без дёргания
      if (scrollRafRef.current) return;
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const delta = scrollTop - lastPageScrollTop.current;

        if (delta > 30) {
          setIsMiniCollapsed(true);
        }

        lastPageScrollTop.current = scrollTop;
      });
    };

    window.addEventListener('scroll', handlePageScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handlePageScroll);
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    };
  }, [isMobile, mode]);

  // Свой сток витрины — из того же места, что и у страницы
  // (`I18nProvider translateInventory`, читается App один раз): флаг по URL на
  // каждом рендере мог разойтись со страницей при смене `?token` в том же
  // документе. В embed провайдера нет — false, как и нужно (ревью !1304).
  const { translateInventory: isOwnStock } = useTranslation();

  // Effective values from config (props override config, contactInfo as fallback)
  const effectiveTitle =
    title ??
    (isOwnStock && !config.WIDGET_TITLE_EXPLICIT
      ? widgetT('askAi', language)
      : config.WIDGET_TITLE);
  const effectiveShowWhatsApp = showWhatsApp ?? config.SHOW_WHATSAPP;
  // Используем contactInfo.whatsapp как единый источник данных (как CarCard и ContactPage)
  const effectiveWhatsAppNumber =
    whatsAppNumber || contactInfo.whatsapp || config.WHATSAPP_NUMBER;
  // Telegram (#1493): тот же единый источник, что и WhatsApp. Кнопку рисуем
  // только при валидном хендле — включённый тумблер без хендла не должен
  // давать кнопку, которая никуда не ведёт.
  const effectiveTelegramHandle =
    telegramHandle || contactInfo.telegram || config.TELEGRAM_HANDLE;
  const effectiveShowTelegram =
    (showTelegram ?? config.SHOW_TELEGRAM) && Boolean(telegramUrl(effectiveTelegramHandle));

  const hasStartedRef = useRef(false);
  const sendMessageRef = useRef(null);
  const isConnectedRef = useRef(false);
  const pendingMessageRef = useRef(null);
  const streamingMessageRef = useRef(null); // For tracking current streaming message ID
  const proactiveMessageRef = useRef(null); // Issue #324: For tracking proactive response message ID
  const sdkLaunchContextRef = useRef(null); // Issue #568: Synchronous launchContext from SDK open() call
  const voiceRetryCountRef = useRef(0); // Issue #566: Retry counter for voice reconnection
  const voiceRetryTimerRef = useRef(null); // Issue #566: Timer for retry delay
  const startConnectionRef = useRef(null); // Issue #566: Ref for latest startConnection (avoids stale closure in retry setTimeout)
  const modeRef = useRef(null); // Issue #566: Ref for latest mode (avoids stale closure in retry setTimeout)
  const isRetryingRef = useRef(false); // Issue #566: Guard against cascade from LiveKitRoom unmount during retry
  const userInitiatedDisconnectRef = useRef(false); // Issue #593: Flag — true ONLY when user explicitly ends call
  const hadActiveSessionRef = useRef(false); // Issue #593: True once agent was connected (distinguishes agent disconnect from peer failure)
  const disconnectHandledRef = useRef(false); // Issue #593: Prevent double handleRoomDisconnected (server ROOM_DELETED + Mechanism A endCall)
  const widgetOpenTimestampRef = useRef(null); // Issue #577: Track widget open time for analytics duration
  const conversationTrackedRef = useRef(false); // Issue #577: Guard against duplicate conversation_start events
  const pendingTextMessagesRef = useRef([]); // Issue #572: Queue for text messages sent during active stream
  const sendHttpMessageRef = useRef(null); // Issue #572: Ref to break circular dep with handleHttpComplete

  const {
    setOpenHandler,
    setOpenChatHandler,
    setCloseHandler,
    setIsolatedCallActive,
    launchContext,
    clearLaunchContext,
    clearInitialMode,
  } = useVoiceAssistantContext();

  const {
    connectionDetails,
    ensureConnectionDetails,
    refreshConnectionDetails,
    clearConnectionDetails,
    isLoading: isFetchingDetails,
  } = useLiveKitConnection();

  // Load saved geometry from localStorage for each mode
  // Use state to allow re-loading when positions are reset
  const [geometryKey, setGeometryKey] = useState(0);
  const savedGeometryCollapsed = loadWidgetGeometry('collapsed');
  const savedGeometryVoice = loadWidgetGeometry('voice');
  const savedGeometryChat = loadWidgetGeometry('chat');

  // Widget bar size for boundary calculations (collapsed/voice modes)
  // Desktop: 300x56, Mobile: dynamic width but we use desktop for simplicity
  const barSize = { width: 300, height: 56 };

  // Draggable hook for COLLAPSED mode (desktop + mobile)
  const {
    position: collapsedPosition,
    handleMouseDown: handleCollapsedDrag,
    handleTouchStart: handleCollapsedTouchStart,
    isDragging: isDraggingCollapsed,
    setPosition: setCollapsedPosition,
  } = useDraggable({
    initialPosition: savedGeometryCollapsed.position,
    elementSize: barSize,
    disabled: false, // Enable drag on both desktop and mobile
    onDragEnd: (newPosition) => {
      saveWidgetGeometry('collapsed', newPosition, null);
    },
  });

  // Draggable hook for VOICE mode (desktop + mobile)
  const {
    position: voicePosition,
    handleMouseDown: handleVoiceDrag,
    handleTouchStart: handleVoiceTouchStart,
    isDragging: isDraggingVoice,
    setPosition: setVoicePosition,
  } = useDraggable({
    initialPosition: savedGeometryVoice.position,
    elementSize: barSize,
    disabled: false, // Enable drag on both desktop and mobile
    onDragEnd: (newPosition) => {
      saveWidgetGeometry('voice', newPosition, null);
    },
  });

  // Draggable hook for CHAT mode (desktop only - mobile is bottom sheet)
  const {
    position: chatPosition,
    handleMouseDown: handleChatDrag,
    handleTouchStart: handleChatTouchStart,
    isDragging: isDraggingChat,
  } = useDraggable({
    initialPosition: savedGeometryChat.position,
    elementSize: savedGeometryChat.size || { width: 450, height: 580 },
    disabled: isMobile, // Disable on mobile - chat is bottom sheet
    onDragEnd: (newPosition) => {
      // eslint-disable-next-line no-use-before-define -- pre-existing: the reference is inside a deferred callback, so it resolves after the declaration (#1206 enabled this rule)
      saveWidgetGeometry('chat', newPosition, size);
    },
  });

  // Thresholds for collapsing
  const COLLAPSE_WIDTH_THRESHOLD = 280;
  const COLLAPSE_HEIGHT_THRESHOLD = 80;

  // Resizable hook (desktop only, chat and voice modes)
  const { size, resizeHandles, isResizing } = useResizable({
    initialSize: savedGeometryChat.size,
    minSize: { width: 100, height: 50 }, // Allow very small sizes for collapse detection
    maxSize: { width: 800, height: 900 },
    disabled: isMobile || mode === 'collapsed',
    onResizeEnd: (newSize) => {
      // Check if should collapse
      if (
        newSize.width <= COLLAPSE_WIDTH_THRESHOLD ||
        newSize.height <= COLLAPSE_HEIGHT_THRESHOLD
      ) {
        setMode('collapsed');
        // Don't save collapsed size - keep previous good size for next open
        return;
      }
      saveWidgetGeometry('chat', chatPosition, newSize);
    },
  });

  // Detect mobile viewport and handle resize for position correction
  useEffect(() => {
    let prevWidth = window.innerWidth;

    const handleResize = () => {
      const currentWidth = window.innerWidth;
      const widthChanged = currentWidth !== prevWidth;
      prevWidth = currentWidth;

      const wasMobile = isMobile;
      const nowMobile = currentWidth <= 768;
      setIsMobile(nowMobile);

      // На мобильном: если изменилась только высота (адресная строка показалась/скрылась),
      // НЕ корректируем позицию виджета — это ложный resize
      if (nowMobile && !widthChanged) {
        return;
      }

      // При реальном resize (поворот экрана, десктоп) проверяем позиции
      // и сбрасываем к дефолтным если виджет за пределами viewport
      if (mode === 'collapsed') {
        const correctedPosition = ensurePositionInViewport(
          collapsedPosition,
          'collapsed'
        );
        if (
          correctedPosition.x !== collapsedPosition.x ||
          correctedPosition.y !== collapsedPosition.y
        ) {
          setCollapsedPosition(correctedPosition);
          saveWidgetGeometry('collapsed', correctedPosition, null);
          console.log(
            '[VoiceWidgetConnected] Corrected collapsed position after resize'
          );
        }
      } else if (mode === 'voice') {
        const correctedPosition = ensurePositionInViewport(
          voicePosition,
          'voice'
        );
        if (
          correctedPosition.x !== voicePosition.x ||
          correctedPosition.y !== voicePosition.y
        ) {
          setVoicePosition(correctedPosition);
          saveWidgetGeometry('voice', correctedPosition, null);
          console.log(
            '[VoiceWidgetConnected] Corrected voice position after resize'
          );
        }
      }
      // Chat mode на мобильном - bottom sheet, позиция не важна

      // При переходе между mobile/desktop сбрасываем к дефолтной позиции
      if (wasMobile !== nowMobile && mode !== 'chat') {
        const defaultGeometry = getDefaultGeometry(mode);
        if (mode === 'collapsed') {
          setCollapsedPosition(defaultGeometry.position);
          saveWidgetGeometry('collapsed', defaultGeometry.position, null);
        } else if (mode === 'voice') {
          setVoicePosition(defaultGeometry.position);
          saveWidgetGeometry('voice', defaultGeometry.position, null);
        }
        console.log(
          `[VoiceWidgetConnected] Reset ${mode} position after mobile/desktop switch`
        );
      }
    };

    // Initial check
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [
    mode,
    isMobile,
    collapsedPosition,
    voicePosition,
    setCollapsedPosition,
    setVoicePosition,
  ]);

  // Track mode changes and reset positions to default
  // При смене состояния виджета всегда сбрасываем позицию к дефолтной
  useEffect(() => {
    if (previousMode !== null && previousMode !== mode) {
      // Режим изменился - сбрасываем позицию к дефолтной для нового режима
      const defaultGeometry = getDefaultGeometry(mode);

      // Update both localStorage AND component state
      saveWidgetGeometry(mode, defaultGeometry.position, defaultGeometry.size);

      // Immediately update the draggable position
      if (mode === 'collapsed') {
        setCollapsedPosition(defaultGeometry.position);
      } else if (mode === 'voice') {
        setVoicePosition(defaultGeometry.position);
      }
      // Chat mode позиция обрабатывается отдельно в handleChatClick

      console.log(
        `[VoiceWidgetConnected] Reset ${mode} position to default after mode change from ${previousMode}`
      );

      // Emit SDK events for mode changes
      if (
        mode === 'collapsed' &&
        (previousMode === 'chat' || previousMode === 'voice')
      ) {
        window.dispatchEvent(new CustomEvent('b2b-demo:widget-close'));

        // Issue #577: Log widget-close to backend + GTM
        const closeDurationMs = widgetOpenTimestampRef.current
          ? Date.now() - widgetOpenTimestampRef.current
          : null;
        if (browserControlWS.isConnected()) {
          browserControlWS.sendUserAction('widget-close', {
            previousMode,
            durationMs: closeDurationMs,
          });
        }
        trackWidgetClose({ previousMode, durationMs: closeDurationMs });
        widgetOpenTimestampRef.current = null;
      } else if (
        (mode === 'chat' || mode === 'voice') &&
        previousMode === 'collapsed'
      ) {
        window.dispatchEvent(new CustomEvent('b2b-demo:widget-open'));

        // Issue #577: Log widget-open to backend + GTM
        widgetOpenTimestampRef.current = Date.now();
        if (browserControlWS.isConnected()) {
          browserControlWS.sendUserAction('widget-open', {
            mode,
            launchContext: launchContext || null,
          });
        }
        trackWidgetOpen({ mode, launchContext });
      }
    }

    // Update previousMode for next transition
    setPreviousMode(mode);
  }, [mode, previousMode, setCollapsedPosition, setVoicePosition]);

  // Sync messages with testing API
  useEffect(() => {
    setMessagesRef(messages);
  }, [messages]);

  // Зеркало переписки для мокапа инбокса рядом (#1330). Публикуем ВСЮ
  // переписку, а не событие отправки: ответ ассистента приходит кусками и
  // «событием» не описывается. Стор сам не будит подписчиков, если ничего не
  // изменилось.
  useEffect(() => {
    if (!mirror) return;
    // Во время изолированного звонка зеркало держит переписку ДО звонка:
    // разговор по телефону в инбокс команды не попадает (#1441).
    chatMirror.publish(isolatedCall ? callSnapshotRef.current : messages);
  }, [mirror, messages, isolatedCall]);

  // Звонок закончился (виджет вышел из голоса — трубку положили, закрыли
  // крестиком, агент отключился, соединение не поднялось): переписка чата
  // возвращается к снимку. В историю звонок не писал — `LiveKitSession`
  // получает `sessionId` только вне звонка.
  useEffect(() => {
    if (!isolatedCall || mode === 'voice') return;
    const snapshot = callSnapshotRef.current || [];
    isolatedCallRef.current = false;
    callSnapshotRef.current = null;
    setIsolatedCall(false);
    setMessages(snapshot);
    if (sessionId) saveMessages(sessionId, snapshot);
  }, [isolatedCall, mode, sessionId]);

  // Карточка звонка узнаёт о конце звонка от виджета, а не только от своей
  // кнопки: трубку мог положить агент, соединение могло не подняться.
  useEffect(() => {
    setIsolatedCallActive?.(isolatedCall);
  }, [isolatedCall, setIsolatedCallActive]);

  // Набранное во время звонка — после возврата чата к снимку (см. выше).
  useEffect(() => {
    if (isolatedCall || mode === 'voice' || !sendAfterCallRef.current) return;
    const queued = sendAfterCallRef.current;
    sendAfterCallRef.current = null;
    // По одному, как набирали: пока идёт ответ, следующее ставится в очередь
    // самим `handleSendMessage` (#572).
    queued.forEach((text) => handleSendMessageRef.current?.(text));
  }, [isolatedCall, mode]);

  // Обратная сторона зеркала: команда отвечает из мокапа панели, и её реплика
  // приходит сюда (#1330). Посетителю она показывается обычным ответом — со
  // стороны сайта это и есть ответ дилера, кто бы его ни написал.
  useEffect(() => {
    if (!mirror) return undefined;
    return chatMirror.subscribeInbound(({ id, text }) => {
      setMessages((prev) => [
        ...prev,
        { id, role: 'assistant', text, timestamp: new Date() },
      ]);
    });
  }, [mirror]);

  // Sync sessionId with testing API
  useEffect(() => {
    if (sessionId) {
      setTestingSessionId(sessionId);
      setAnalyticsSessionId(sessionId);
      setBeaconSessionId(sessionId);
    }
  }, [sessionId]);

  // Issue #577: Initialize analytics bridge on mount
  useEffect(() => {
    initAnalytics({
      sessionId,
      organizationId: config.ORGANIZATION_ID,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Сбрасываем индикатор загрузки когда подключение установлено
  useEffect(() => {
    if (sessionState.status === 'connected' && isModeChanging) {
      console.log(
        '[VoiceWidgetConnected] Connection established, hiding loading indicator'
      );
      setIsModeChanging(false);
    }

    // Emit SDK ready event when first connected
    if (sessionState.status === 'connected' && !hasStartedRef.current) {
      window.dispatchEvent(new CustomEvent('b2b-demo:ready'));
    }

    // Issue #577: Track conversation start for voice mode (once per session)
    if (
      sessionState.status === 'connected' &&
      !conversationTrackedRef.current
    ) {
      conversationTrackedRef.current = true;
      trackConversationStart({ mode: 'voice' });
    }
  }, [sessionState.status, isModeChanging]);

  // Block body scroll when widget is maximized on mobile
  useEffect(() => {
    if (!isMobile || widgetState !== 'maximized') return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMobile, widgetState]);

  // Wrapper для onMessagesChange который предотвращает сброс истории при disconnect/reconnect
  const handleMessagesChange = useCallback(
    (newMessages) => {
      if (newMessages.length === 0) {
        if (isConnectedRef.current) {
          console.log(
            '[VoiceWidgetConnected] Ignoring empty messages from LiveKit disconnect'
          );
          return;
        }
        if (messages.length > 0) {
          console.log(
            '[VoiceWidgetConnected] Ignoring empty messages from initial render'
          );
          return;
        }
      } else {
        if (!isConnectedRef.current && newMessages.length > 0) {
          isConnectedRef.current = true;
        }
      }
      setMessages(newMessages);

      // Emit SDK event for new messages
      if (newMessages.length > messages.length) {
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage) {
          window.dispatchEvent(
            new CustomEvent('b2b-demo:message', {
              detail: {
                id: lastMessage.id,
                role: lastMessage.role,
                text: lastMessage.text,
                timestamp: lastMessage.timestamp,
              },
            })
          );
        }
      }
    },
    [messages.length]
  );

  // Issue #572: Drain queued text messages after current stream completes.
  // Uses refs only (no hook deps), called from handleHttpComplete and handleHttpError.
  // This mirrors the backend session lock — one stream at a time.
  const drainMessageQueue = useCallback(() => {
    if (pendingTextMessagesRef.current.length === 0) return;

    // #1485: an entry a reload carried over shows its bubble again — the restored page's
    // history comes from the checkpoint, which never saw the message.
    const { text: nextMessage, restored } = queueEntry(
      pendingTextMessagesRef.current.shift()
    );
    console.log(
      '[VoiceWidgetConnected] Draining queue, next message:',
      nextMessage.substring(0, 50)
    );

    const assistantMsgId = generateMessageId('assistant');
    streamingMessageRef.current = assistantMsgId;

    setMessages((prev) => [
      ...prev,
      ...(restored
        ? [
            {
              id: generateMessageId('user'),
              role: 'user',
              text: nextMessage,
              timestamp: new Date(),
            },
          ]
        : []),
      {
        id: assistantMsgId,
        role: 'assistant',
        text: '',
        timestamp: new Date(),
        isStreaming: true,
      },
    ]);

    sendHttpMessageRef.current?.(nextMessage);
  }, []);

  // #1485: a reload no longer waits for the answer holding these back, so the executor puts
  // them in the restore data and the restored page sends them.
  useEffect(() => {
    commandExecutor.setPendingMessagesProvider(() =>
      pendingTextMessagesRef.current.map((entry) => queueEntry(entry).text)
    );
    return () => commandExecutor.setPendingMessagesProvider(null);
  }, []);

  // HTTP API хук для текстового чата (когда WebRTC не подключен)
  // ВАЖНО: msgId захватывается ДО вызова setMessages, чтобы избежать race condition
  // когда handleHttpComplete устанавливает streamingMessageRef.current = null
  // до того как React выполнит callback setMessages
  const handleHttpMessage = useCallback((chunk, accumulated) => {
    // Захватываем msgId СЕЙЧАС, пока stream еще активен
    const msgId = streamingMessageRef.current;
    if (!msgId) {
      console.warn(
        '[VoiceWidgetConnected] handleHttpMessage: No streaming message ID, skipping'
      );
      return;
    }

    // Обновляем streaming сообщение используя захваченный msgId
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.id === msgId);
      if (idx === -1) return prev;

      const updated = [...prev];
      updated[idx] = { ...updated[idx], text: accumulated };
      return updated;
    });
  }, []);

  // ВАЖНО: msgId захватывается ДО вызова setMessages (аналогично другим handlers)
  const handleHttpError = useCallback(
    (error) => {
      console.error('[VoiceWidgetConnected] HTTP chat error:', error);
      // Issue #581: Reset proactive streaming on error (covers proactive stream errors)
      commandExecutor.setProactiveStreaming(false);

      // Захватываем msgId СЕЙЧАС, до setMessages
      const msgId = streamingMessageRef.current;
      if (!msgId) {
        // Issue #572: Even without active stream, drain queue
        drainMessageQueue();
        return;
      }

      // Обновляем streaming сообщение с ошибкой
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === msgId);
        if (idx === -1) return prev;

        const updated = [...prev];
        updated[idx] = {
          ...updated[idx],
          text: updated[idx].text || `Error: ${error.message}`,
          error: true,
        };
        return updated;
      });
      streamingMessageRef.current = null;
      // Issue #572: Process next queued message
      drainMessageQueue();
    },
    [drainMessageQueue]
  );

  // Issue #752: Handle suggestions from backend (chat-stream-end / proactive-stream-chunk done)
  //
  // Note: we used to compare `messageId` (server-echoed WS messageId) against
  // `latestAssistantMsgIdRef.current` (frontend-generated placeholder id, e.g.
  // `greeting-xxx` / `assistant-xxx`) as a stale-suggestion guard. Those IDs
  // come from different spaces and never match, so the guard silently
  // discarded every real backend suggestion. The outer `useTextChat` hook
  // already filters incoming WS frames by `currentMessageIdRef`, so stale
  // suggestions for previous turns cannot reach this callback. Plus
  // `setSuggestions([])` in `handleSendMessage` clears them on new user
  // input, so there is no realistic race we still need to guard against here.
  const handleSuggestions = useCallback((items, messageId) => {
    console.log('[Suggestions] shown:', { count: items.length, messageId });
    setSuggestions(items);
    setSuggestionsLoading(false);
  }, []);

  // ВАЖНО: msgId захватывается ДО вызова setMessages (аналогично другим handlers)
  const handleHttpComplete = useCallback(
    (finalText) => {
      console.log(
        '[VoiceWidgetConnected] HTTP chat completed, text length:',
        finalText.length
      );

      // Захватываем msgId СЕЙЧАС, до любых setMessages
      const msgId = streamingMessageRef.current;
      if (!msgId) {
        streamingMessageRef.current = null;
        // Issue #572: Drain queue even without active stream
        drainMessageQueue();
        return;
      }

      // Issue #461: Если HTTP response пустой - УДАЛЯЕМ сообщение вместо показа пустого пузыря
      // Это происходит когда агент возвращает empty response (например, recent interaction)
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === msgId);
        if (idx === -1) return prev;

        // Проверяем есть ли контент в сообщении
        if (!prev[idx].text || prev[idx].text.trim().length === 0) {
          console.log(
            '[VoiceWidgetConnected] Removing empty HTTP message:',
            msgId
          );
          return prev.filter((m) => m.id !== msgId);
        }

        // Завершаем сообщение с контентом
        const updated = [...prev];
        updated[idx] = { ...updated[idx], isStreaming: false };
        return updated;
      });

      // Сохраняем в localStorage
      if (sessionId) {
        setMessages((prev) => {
          saveMessages(sessionId, prev);
          return prev;
        });
      }
      streamingMessageRef.current = null;
      // Issue #752: Track latest assistant message — used by handleSuggestions stale guard
      latestAssistantMsgIdRef.current = msgId;
      // Issue #572: Process next queued message
      drainMessageQueue();
    },
    [sessionId, drainMessageQueue]
  );

  // Issue #324: Proactive response handlers (after async tool completion)
  // Proactive response отображается как НОВОЕ сообщение ассистента
  const handleProactiveStart = useCallback((runId, requestMessageId) => {
    console.log('[VoiceWidgetConnected] Proactive stream started:', runId);
    // Issue #581: Notify commandExecutor that proactive stream is active
    commandExecutor.setProactiveStreaming(true);
    // Создаём НОВОЕ сообщение ассистента для proactive response. #1485: a handed-over answer is
    // named after its request, so a second tab that took it over too stores the same message.
    const msgId = proactiveMessageId(runId, requestMessageId);
    proactiveMessageRef.current = msgId;

    const proactiveMessage = {
      id: msgId,
      role: 'assistant',
      text: '',
      timestamp: new Date(),
      isStreaming: true,
      isProactive: true, // Метка что это proactive response
    };

    setMessages((prev) =>
      prev.some((m) => m.id === msgId) ? prev : [...prev, proactiveMessage]
    );
  }, []);

  // ВАЖНО: msgId захватывается ДО вызова setMessages (аналогично handleHttpMessage)
  const handleProactiveChunk = useCallback((chunk, accumulated) => {
    // Захватываем msgId СЕЙЧАС, пока stream еще активен
    const msgId = proactiveMessageRef.current;
    if (!msgId) return;

    // Обновляем proactive сообщение используя захваченный msgId
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.id === msgId);
      if (idx === -1) return prev;

      const updated = [...prev];
      updated[idx] = { ...updated[idx], text: accumulated };
      return updated;
    });
  }, []);

  // ВАЖНО: msgId захватывается ДО вызова setMessages (аналогично другим handlers)
  const handleProactiveDone = useCallback(
    (finalText) => {
      console.log(
        '[VoiceWidgetConnected] Proactive stream completed, text length:',
        finalText.length
      );
      // Issue #581: Notify commandExecutor that proactive stream ended
      commandExecutor.setProactiveStreaming(false);

      // Захватываем msgId СЕЙЧАС, до любых setMessages
      const msgId = proactiveMessageRef.current;
      if (!msgId) return;

      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === msgId);
        if (idx === -1) return prev;

        // Issue #324: Если proactive response пустой - УДАЛЯЕМ сообщение вместо показа пустого пузыря
        // Это происходит когда агент уже ответил в основном HTTP stream
        if (!prev[idx].text || prev[idx].text.trim().length === 0) {
          console.log(
            '[VoiceWidgetConnected] Removing empty proactive message:',
            msgId
          );
          return prev.filter((m) => m.id !== msgId);
        }

        // Завершаем сообщение с контентом
        const updated = [...prev];
        updated[idx] = { ...updated[idx], isStreaming: false };
        return updated;
      });

      // Сохраняем в localStorage
      if (sessionId) {
        setMessages((prev) => {
          saveMessages(sessionId, prev);
          return prev;
        });
      } else if (browserControlWS.sessionId) {
        // #1485: b2b-api hands an answer to a page that has not loaded the chat yet — it
        // restored collapsed, or has no sessionId until it registers. Its state does not hold
        // the conversation, so the answer is added to the stored one, not saved over it.
        const storageSid = browserControlWS.sessionId;
        setMessages((prev) => {
          appendToStoredMessages(storageSid, prev.find((m) => m.id === msgId));
          return prev;
        });
      }
      proactiveMessageRef.current = null;
      // Issue #752: Track latest assistant message — used by handleSuggestions stale guard
      latestAssistantMsgIdRef.current = msgId;
    },
    [sessionId]
  );

  const {
    sendMessage: sendHttpMessage,
    isLoading: isHttpLoading,
    isApiAvailable: isHttpApiAvailable,
  } = useTextChat({
    sessionId,
    textChatVersion, // Issue #496: Счётчик версии для переинициализации хука при смене режима voice/text
    workflowType: config.WORKFLOW_TYPE || 'MAIN_WORKFLOW',
    inventoryId: config.INVENTORY_ID,
    onMessage: handleHttpMessage,
    onError: handleHttpError,
    onComplete: handleHttpComplete,
    // Issue #324: Proactive response handlers (after async tool completion)
    onProactiveStart: handleProactiveStart,
    onProactiveChunk: handleProactiveChunk,
    onProactiveDone: handleProactiveDone,
    // Issue #752: Auto-suggestions
    onSuggestions: handleSuggestions,
    // #1485: only a page the agent's navigation reloaded takes an answer before its sessionId
    allowProactiveBeforeSession: isNavigationRestoreRef.current,
  });

  // Issue #572: Keep ref in sync for drainMessageQueue (avoids circular dependency)
  sendHttpMessageRef.current = sendHttpMessage;

  // Issue #735: Pre-request microphone permission before starting voice session.
  // Called from user-action handlers (handleMicClick, handleConsentConfirm) BEFORE
  // startConnection(), so the browser permission dialog is resolved before the room
  // is created and agent is dispatched. This prevents the agent's greeting from being
  // "swallowed" while the user hasn't clicked "Allow" yet.
  // For retries/reconnects, browser caches the permission so this resolves instantly.
  const requestMicPermission = useCallback(async () => {
    try {
      console.warn('[735] Requesting microphone permission...');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      stream.getTracks().forEach((track) => track.stop());
      console.warn('[735] Microphone permission granted, stream released');
    } catch (micError) {
      console.warn('[735] Microphone permission denied or failed:', micError);
    }
  }, []);

  // Start connection
  const startConnection = useCallback(async () => {
    console.warn('[566-DEBUG] startConnection() ENTER', {
      hasConnectionDetails: !!connectionDetails,
      sessionStatus: sessionState.status,
      connectionError,
      shouldConnect,
      roomKey,
    });

    if (connectionDetails && sessionState.status === 'connected') {
      console.warn('[566-DEBUG] startConnection: SKIP — already connected');
      return;
    }

    isConnectedRef.current = false;

    if (sessionState.status === 'disconnected' || connectionError) {
      console.warn(
        '[566-DEBUG] startConnection: incrementing roomKey (status:',
        sessionState.status,
        ', error:',
        connectionError,
        ')'
      );
      setRoomKey((prev) => prev + 1);
    }

    setConnectionError(null);
    setIsInitializing(true);

    try {
      // Изолированный звонок (#1441) — своя сессия агента: иначе чекпоинт
      // общий, и разговор по телефону вернулся бы в чат историей. Сессию
      // чата (state, WS, localStorage) звонок не трогает.
      const isolated = isolatedCallRef.current;
      const gen = callGenRef.current;
      const baseId = getOrCreateSessionId();
      const id = isolated ? `${baseId}-call-${Date.now().toString(36)}` : baseId;
      console.warn('[566-DEBUG] startConnection: sessionId:', id);
      if (!isolated) {
        setSessionId(id);
        refreshSession();

        // Load message history from localStorage
        setMessages((currentMessages) => {
          if (currentMessages.length > 0) {
            return currentMessages;
          }

          const mappedMessages = loadAndMapMessages(id);
          return mappedMessages;
        });
      }

      // Connect to browser control WebSocket. Звонку сайт не нужен, а
      // переподключение на его сессию оторвало бы WS чата.
      if (!isolated) {
        try {
          await browserControlWS.connect(getWsUrl(), id);

          // Verify connection was established (race condition fix)
          if (!browserControlWS.isConnected()) {
            throw new Error('WebSocket not connected after connect() resolved');
          }

          const { getCurrentPageContext } = await import(
            '../../utils/pageContextParser'
          );
          const pageContext = getCurrentPageContext();
          sendPageContextWithLaunch(
            pageContext,
            launchContext,
            clearLaunchContext
          );
        } catch (wsError) {
          console.error(
            '[VoiceWidgetConnected] Browser control WebSocket error:',
            wsError
          );
        }
      }

      const currentDate = getCurrentDateTime();

      // Issue #703: Pass tool_cycle_continue or page_navigated launch_context for voice tool observation.
      // AUTODIS #904: when neither restore-action applies, forward the widget's
      // current launchContext as-is (e.g. {action:'ask_about_car', metadata:{carId,...}}).
      // Voice runner reads launch_context.metadata.carId identically to the HTTP
      // path (agent.py:198 request.launch_context.metadata.carId), so the same
      // middleware (before_model_context → wrap_tool_call) injects car_id into
      // lookup_car_info without any voice-specific code.
      // #1362: голос язык витрины НЕ несёт, и это осознанно. На LiveKit-пути
      // его сейчас некому прочитать: `assistent_builder` зовёт
      // `build_greeting_instruction(launch_context=None)` без language_code,
      // `get_default_entrance(..., VOICE)` язык игнорирует (таблица i18n
      // гейтится на TEXT), а генератор пилюль для голоса вообще не включается
      // (`configurable["suggestions"]` LiveKit не выставляет). Штамп был бы
      // мёртвым кодом — и всё равно не сработал бы на самом частом входе, где
      // это выражение равно `undefined`. Голосовое приветствие на выбранном
      // языке — отдельная задача: ему нужен ещё и TTS-голос под локаль.
      const voiceLaunchContext = cachedRestoreData?.toolCycleContinue
        ? {
            action: 'tool_cycle_continue',
            targetPage: cachedRestoreData.targetPage,
          }
        : cachedRestoreData?.targetPage
        ? { action: 'page_navigated', targetPage: cachedRestoreData.targetPage }
        : launchContext || undefined;
      // Отменили ещё до запроса токена — не создаём ни комнату, ни запись звонка.
      if (callGenRef.current !== gen) {
        console.warn('[566-DEBUG] startConnection: cancelled before fetching details');
        return;
      }
      console.warn(
        '[566-DEBUG] startConnection: calling ensureConnectionDetails with sessionId:',
        id,
        voiceLaunchContext ? ', launchContext:' : '',
        voiceLaunchContext || ''
      );
      const result = await ensureConnectionDetails({
        sessionId: id,
        browserControlEnabled: !isolated,
        currentDate: currentDate,
        workflowType: config.WORKFLOW_TYPE,
        inventoryId: config.INVENTORY_ID,
        organizationId: config.ORGANIZATION_ID,
        ...(voiceLaunchContext && {
          launchContext: JSON.stringify(voiceLaunchContext),
        }),
        // Issue #728: Removed skipDispatch for navigation restore.
        // With delete_room_on_close=True, old room is already destroyed after navigation —
        // agent must be dispatched again. skipDispatch caused ~65s reconnect delay.
      });
      console.warn(
        '[566-DEBUG] startConnection: ensureConnectionDetails returned:',
        {
          serverUrl: result?.serverUrl,
          hasToken: !!result?.participantToken,
          tokenLength: result?.participantToken?.length,
          sessionId: result?.sessionId,
        }
      );

      // Трубку положили, пока ждали токен: комнату не поднимаем (#1441).
      if (callGenRef.current !== gen) {
        console.warn('[566-DEBUG] startConnection: cancelled while fetching details');
        return;
      }
      console.warn('[566-DEBUG] startConnection: setting shouldConnect=true');
      setShouldConnect(true);
    } catch (error) {
      console.warn('[566-DEBUG] startConnection: ERROR:', error);
      console.error('[VoiceWidgetConnected] startConnection error:', error);
      // Звонок с карточки не поднялся: во время звонка экран показывает чат и
      // ошибку не видно — завершаем звонок, чтобы карточка это узнала (#1441).
      if (isolatedCallRef.current) setMode('chat');
      setConnectionError(
        error instanceof Error ? error.message : 'Unknown error'
      );
      trackError({
        errorType: 'connection_error',
        message: error instanceof Error ? error.message : 'Unknown error',
      });
      setShouldConnect(false);
    } finally {
      console.warn('[566-DEBUG] startConnection: DONE, isInitializing=false');
      setIsInitializing(false);
    }
  }, [
    connectionDetails,
    sessionState.status,
    connectionError,
    ensureConnectionDetails,
    launchContext,
    clearLaunchContext,
  ]);

  /**
   * Reset LiveKit session state
   * @param {boolean} keepWebSocket - Issue #496: If true, don't disconnect browserControlWS
   *   (needed when switching to text chat mode - WebSocket is required for text chat)
   * @param {boolean} clearDetails - Issue #566: If false, keep connectionDetails (needed for retry
   *   so LiveKitRoom stays mounted and can reconnect with fresh token)
   */
  const resetState = useCallback(
    (keepWebSocket = false, clearDetails = true) => {
      console.warn(
        '[566-DEBUG] resetState() called, keepWebSocket:',
        keepWebSocket,
        ', clearDetails:',
        clearDetails,
        ', caller:',
        new Error().stack?.split('\n')[2]?.trim()
      );
      setShouldConnect(false);
      setSessionState({
        status: 'disconnected',
        isSpeaking: false,
        isMuted: false,
        micPending: false,
      });
      hadActiveSessionRef.current = false; // Issue #593: Reset for next session
      disconnectHandledRef.current = false; // Issue #593: Reset for next session

      // Issue #510: Clear connection details to force fresh token on next connect
      // This prevents stale token from being reused when user restarts voice session
      // Issue #566: Skip clearing during retry to avoid unmounting LiveKitRoom
      if (clearDetails) {
        clearConnectionDetails();
      }

      // Issue #496: Don't disconnect WebSocket when switching to text chat mode
      if (!keepWebSocket && browserControlWS.isConnected()) {
        browserControlWS.disconnect();
      }
    },
    [clearConnectionDetails]
  );

  // Issue #566: Keep refs in sync for retry setTimeout (avoids stale closures)
  useEffect(() => {
    startConnectionRef.current = startConnection;
  }, [startConnection]);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  // Auto-start on mount if initialMode is not collapsed
  useEffect(() => {
    let cancelled = false; // Issue #566: prevent state updates after unmount

    console.warn(
      '[566-DEBUG] Auto-start effect fired, hasStartedRef:',
      hasStartedRef.current
    );

    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      // Используем сохранённый режим из ref (вычислен один раз при первом render)
      const initMode = initialModeRef.current;
      console.warn(
        '[566-DEBUG] Auto-start: initMode:',
        initMode,
        ', isNavigationRestore:',
        isNavigationRestoreRef.current
      );

      // Voice mode: connect to LiveKit
      if (initMode === 'voice') {
        console.warn(
          '[566-DEBUG] Auto-start: calling startConnection() for voice mode'
        );
        startConnection();
      }
      // Chat mode: connect only to WebSocket (no LiveKit)
      else if (initMode === 'chat') {
        const sid = sessionId || getOrCreateSessionId();

        // Issue #566: Navigation restore - need to reinitialize text chat and trigger greeting
        if (isNavigationRestoreRef.current) {
          console.log(
            '[VoiceWidgetConnected] Chat mode navigation restore: triggering auto-greeting'
          );
          setTextChatVersion((v) => v + 1);

          // Connect WebSocket and send greeting (async IIFE)
          // Issue #583: setSessionId is deferred to AFTER the critical sendHttpMessage call.
          // Setting it before the IIFE caused a race condition: sessionId change triggered
          // useEffect re-run → cleanup set cancelled=true → IIFE aborted before sending
          // page_navigated launch_context.
          (async () => {
            try {
              // Issue #724: Подписываемся на 'registered' ACK ДО connect(),
              // чтобы не пропустить быстрый ответ от бэкенда (race-safe).
              const registeredPromise = browserControlWS.waitForContextAck(
                'registered',
                null,
                5000
              );

              await browserControlWS.connect(getWsUrl(), sid);
              if (cancelled) return;
              if (!browserControlWS.isConnected()) {
                console.error(
                  '[VoiceWidgetConnected] WebSocket not connected after restore connect'
                );
                return;
              }

              // Issue #724: Ждём 'registered' ACK — гарантия что actionsMap сохранена на бэкенде
              const registeredAck = await registeredPromise;
              console.log(
                '[VoiceWidgetConnected][#724] Registered ACK:',
                registeredAck
              );
              if (cancelled) return;

              // Issue #724: Отправляем page context и ждём ACK
              const { getCurrentPageContext } = await import(
                '../../utils/pageContextParser'
              );
              const pageContext = getCurrentPageContext();
              const pageCtxResult =
                await browserControlWS.sendPageContextWithAck(
                  pageContext,
                  5000
                );
              console.log(
                '[VoiceWidgetConnected][#724] Page context ACK:',
                pageCtxResult
              );

              if (cancelled) return;

              // Create placeholder for agent response
              const assistantMsgId = generateMessageId('greeting');
              streamingMessageRef.current = assistantMsgId;
              setMessages((prev) => [
                ...prev,
                {
                  id: assistantMsgId,
                  role: 'assistant',
                  text: '',
                  timestamp: new Date(),
                  isStreaming: true,
                },
              ]);

              // Issue #719: Wait for React to re-render and useTextChat to re-subscribe
              // to WS events. Without this delay, chat-stream-chunk events arrive before
              // useTextChat handlers are attached, causing the response to be lost.
              await new Promise((resolve) => {
                requestAnimationFrame(() => setTimeout(resolve, 100));
              });

              if (cancelled) return;

              // Trigger auto-greeting / tool observation after reconnect
              // Use ref to get latest sendHttpMessage (avoids stale closure)
              if (!cancelled && sendHttpMessageRef.current) {
                // #1485: messages typed on the page being left and never sent go out after the
                // navigation's answer, as the queue drains. Taken once.
                pendingTextMessagesRef.current.push(
                  ...restoredQueueEntries(cachedRestoreData)
                );
                if (cachedRestoreData?.pendingMessages) {
                  cachedRestoreData = { ...cachedRestoreData, pendingMessages: undefined };
                }
                // Issue #703: Use tool_cycle_continue when restoring after go_website_page
                // to signal backend that this is a tool call cycle continuation, not a new greeting.
                // Fallback to page_navigated for backward compatibility with old restore data.
                const launchAction = cachedRestoreData?.toolCycleContinue
                  ? {
                      action: 'tool_cycle_continue',
                      targetPage: cachedRestoreData.targetPage,
                    }
                  : cachedRestoreData?.targetPage
                  ? {
                      action: 'page_navigated',
                      targetPage: cachedRestoreData.targetPage,
                    }
                  : { action: 'widget_opened' };
                // Issue #587: Diagnostic log for duplicate request investigation
                console.warn(
                  '[VoiceWidgetConnected][#703] IIFE sending launch_context:',
                  {
                    action: launchAction.action,
                    targetPage: launchAction.targetPage,
                    toolCycleContinue: !!cachedRestoreData?.toolCycleContinue,
                    timestamp: Date.now(),
                    source: 'auto-start-IIFE',
                  }
                );
                await sendHttpMessageRef.current('', {
                  launchContext: launchAction,
                });
              }
            } catch (err) {
              if (!cancelled) {
                console.error(
                  '[VoiceWidgetConnected] Chat restore greeting error:',
                  err
                );
              }
            } finally {
              // Issue #522: Set sessionId AFTER async work completes
              // to avoid useEffect re-run cancelling the IIFE
              if (!cancelled && !sessionId) {
                setSessionId(sid);
              }
            }
          })();
        } else {
          // Non-restore path: set sessionId immediately (no async IIFE to cancel)
          // Issue #522: Сохраняем sessionId в state для запроса истории чата
          if (!sessionId) {
            setSessionId(sid);
          }
          browserControlWS.connect(getWsUrl(), sid).catch((err) => {
            console.warn(
              '[VoiceWidgetConnected] Initial WebSocket connect warning:',
              err
            );
          });
        }
      }
    }

    setOpenHandler(() => startConnection);

    commandExecutor.setOnAddMessage((message) => {
      setMessages((prev) => {
        const updatedMessages = [...prev, message];
        if (sessionId) {
          saveMessages(sessionId, updatedMessages);
        }
        return updatedMessages;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [startConnection, setOpenHandler, sessionId]);

  // Issue #593: Track when agent becomes active (for disconnect detection)
  // hadActiveSessionRef distinguishes agent disconnect (agent was active, then room closed)
  // from peer connection failure (agent never connected, connection dropped)
  useEffect(() => {
    if (sessionState.status === 'connected') {
      hadActiveSessionRef.current = true;
    }
  }, [sessionState.status]);

  // Issue #566: Cleanup retry timer on unmount
  useEffect(() => {
    return () => {
      if (voiceRetryTimerRef.current) {
        clearTimeout(voiceRetryTimerRef.current);
        voiceRetryTimerRef.current = null;
      }
    };
  }, []);

  // Issue #566: Sync widget mode to commandExecutor for navigation restore
  // Issue #583: voice-in-chat has mode='chat' + isVoiceInChat=true.
  // Use 'voice' as effective mode so commandExecutor waits for speech end
  // and saves correct restore mode in sg_restore_widget.
  useEffect(() => {
    const effectiveMode = mode === 'chat' && isVoiceInChat ? 'voice' : mode;
    commandExecutor.setCurrentMode(effectiveMode);
  }, [mode, isVoiceInChat]);

  // Issue #580: Sync agent speaking state to commandExecutor
  // Used to defer go_website_page until agent finishes speaking
  useEffect(() => {
    commandExecutor.setAgentSpeaking(sessionState.isSpeaking);
  }, [sessionState.isSpeaking]);

  // Issue #581: Sync text streaming state to commandExecutor
  // Used to defer go_website_page until text stream finishes
  useEffect(() => {
    commandExecutor.setTextStreaming(isHttpLoading);
  }, [isHttpLoading]);

  // React to initialMode prop changes (e.g., from Talk AI button or
  // Search → Sparkles AI button via Chat.jsx prop wiring, см. #922).
  // Этот effect НЕ проверяет restore флаги - только реагирует на изменения prop.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally depends only on initialMode prop changes
  useEffect(() => {
    // Skip initial mount (handled by auto-start useEffect above)
    if (!hasStartedRef.current) {
      return;
    }

    // Преобразуем prop в режим (без restore логики - она только при mount)
    const requestedMode =
      initialMode === 'text' ? 'chat' : initialMode || 'collapsed';
    // #1353: у text-only дилера этот путь тоже ведёт в чат. Он приходит от
    // VoiceAssistantContext (кнопка «Ask the assistant» на главной), минуя
    // openChat/handleMicClick — без гейта здесь prop 'voice' поднял бы
    // LiveKit-сессию в обход всех остальных.
    const newMode = degradeVoiceMode(requestedMode, voiceEnabled);

    if (newMode !== mode && newMode !== 'collapsed') {
      setMode(newMode);

      // Connect to WebSocket for chat mode
      if (newMode === 'chat') {
        const sid = sessionId || getOrCreateSessionId();
        browserControlWS.connect(getWsUrl(), sid).catch((err) => {
          console.warn(
            '[VoiceWidgetConnected] WebSocket connect on mode change:',
            err
          );
        });
      }
      // Start LiveKit connection for voice mode
      else if (newMode === 'voice') {
        startConnection();
      }

      // После consume — сбросить initialMode в context'е, чтобы повторный
      // клик с тем же режимом (Sparkles AI после закрытия chat'а) снова
      // триггернул этот effect. Без сброса `setInitialMode('text')` остаётся
      // no-op (React state setter с тем же value не вызывает re-render).
      clearInitialMode?.();
    }
  }, [initialMode]);

  // Load message history from checkpoints when WebSocket connects
  useEffect(() => {
    const handleChatHistory = (data) => {
      console.log(
        '[VoiceWidgetConnected] Received chat history from checkpoint:',
        {
          sessionId: data.sessionId,
          messagesCount: data.messages?.length || 0,
          totalCount: data.totalCount,
          error: data.error,
          roomName: data.roomName,
        }
      );

      // Логируем ошибки или пустые ответы
      if (data.error) {
        console.warn('[VoiceWidgetConnected] Chat history error:', data.error);
      }

      if (!data.messages || data.messages.length === 0) {
        console.log(
          '[VoiceWidgetConnected] No messages in checkpoint (new session or no history)'
        );
        return;
      }

      if (data.messages && data.messages.length > 0) {
        // Преобразуем сообщения из checkpoint формата в UI формат
        const checkpointMessages = data.messages.map((msg) => ({
          id: msg.id || Date.now() + Math.random(),
          role: msg.role,
          text: msg.text,
          timestamp: new Date(msg.timestamp),
          fromCheckpoint: true, // Метка что сообщение из checkpoint
        }));

        // Слияние — в `checkpointHistory`: checkpoint хранит ответ агента
        // кусками, а живой чат склеивает их в одно сообщение, и сравнение по
        // точному тексту давало дубль над вопросом пользователя (#1417).
        setMessages((prev) => {
          const merged = mergeCheckpointMessages(prev, checkpointMessages);
          if (merged !== prev) {
            console.log(
              `[VoiceWidgetConnected] Merged checkpoint history: ${prev.length} → ${merged.length} messages`
            );
          }
          return merged;
        });
      }
    };

    // Подписываемся на событие chat-history
    browserControlWS.onMessage('chat-history', handleChatHistory);

    return () => {
      browserControlWS.offMessage('chat-history', handleChatHistory);
    };
  }, []);

  // Request chat history when WebSocket connects and we have sessionId
  useEffect(() => {
    console.log('[VoiceWidgetConnected] Chat history useEffect:', {
      sessionId: sessionId || '(null)',
      isHttpApiAvailable,
      willRequest: Boolean(sessionId && isHttpApiAvailable),
    });

    if (sessionId && isHttpApiAvailable) {
      console.log(
        '[VoiceWidgetConnected] Requesting chat history from checkpoint (WebSocket connected)'
      );
      const result = browserControlWS.getChatHistory();
      console.log('[VoiceWidgetConnected] getChatHistory returned:', result);
    }
  }, [sessionId, isHttpApiAvailable]);

  // Microphone control
  const handleMuteToggle = useCallback(async () => {
    // В chat режиме микрофон включает/выключает voice в чате
    if (mode === 'chat') {
      if (!isVoiceInChat) {
        // Включить voice в чате
        setIsVoiceInChat(true);
        setSpeakerMuted(false);
        if (sessionState.status === 'connected') {
          try {
            await controls.toggleMute(true); // unmute
          } catch (error) {
            console.error(
              '[VoiceWidgetConnected] Failed to unmute microphone:',
              error
            );
          }
        }
      } else {
        // Выключить voice в чате
        setIsVoiceInChat(false);
        setSpeakerMuted(true);
        if (sessionState.status === 'connected') {
          try {
            await controls.toggleMute(false); // mute
          } catch (error) {
            console.error(
              '[VoiceWidgetConnected] Failed to mute microphone:',
              error
            );
          }
        }
      }
      return;
    }

    // В других режимах просто toggle микрофона
    try {
      await controls.toggleMute();
    } catch (error) {
      console.error(
        '[VoiceWidgetConnected] Failed to toggle microphone:',
        error
      );
    }
  }, [controls, mode, isVoiceInChat, sessionState.status]);

  // Speaker control
  const toggleSpeakerMute = useCallback(() => {
    setSpeakerMuted((prev) => !prev);
  }, []);

  // Close voice in chat (return to text input)
  // ВАЖНО: Полностью отключаем LiveKit соединение чтобы агент не ждал ответа
  const handleVoiceClose = useCallback(async () => {
    console.log(
      '[VoiceWidgetConnected] handleVoiceClose: disconnecting LiveKit and switching to text mode'
    );
    setIsVoiceInChat(false);
    setSpeakerMuted(true);

    // Отключаем LiveKit room полностью (не просто мутим микрофон)
    // Это нужно чтобы агент получил disconnect event и не спрашивал "вы ещё тут?"
    if (sessionState.status === 'connected') {
      try {
        userInitiatedDisconnectRef.current = true; // Issue #593: Mark as user-initiated
        await controls.endCall();
        console.log(
          '[VoiceWidgetConnected] LiveKit disconnected on voice close'
        );
      } catch (error) {
        console.error(
          '[VoiceWidgetConnected] Failed to disconnect LiveKit:',
          error
        );
      }
    }

    // Уведомляем агента о смене режима через WebSocket
    if (browserControlWS.isConnected()) {
      browserControlWS.sendUserAction('mode-change', {
        from: 'voice',
        to: 'text',
        reason: 'user_closed_voice',
      });
    }
    trackModeSwitch({ from: 'voice', to: 'text' });
  }, [sessionState.status, controls]);

  // Send text message
  // When user sends text message, mute speaker so agent responds with text only
  // Priority depends on mode:
  // - Text mode (chat without voice): ALWAYS use WebSocket/HTTP API
  // - Voice mode (voice or voice-in-chat): use LiveKit DataChannel
  const handleSendMessage = useCallback(
    async (text) => {
      // Написали в чат во время звонка (клавиатура, скринридер — без касания
      // окна): звонок завершается, а текст уходит в чат, когда тот вернётся к
      // переписке до звонка. Иначе он ушёл бы агенту звонка и пропал (#1441).
      if (isolatedCallRef.current) {
        sendAfterCallRef.current = [...(sendAfterCallRef.current || []), text];
        closeInlineVoiceRef.current?.();
        return;
      }
      console.log('[VoiceWidgetConnected] Sending text:', text);

      // Issue #752: Clear suggestions when user sends a message
      setSuggestions([]);
      setSuggestionsLoading(false);

      // Mute speaker for text-only response
      setSpeakerMuted(true);

      // Add message to UI IMMEDIATELY (UX: user sees their message right away)
      const userMessage = {
        id: generateMessageId('user'),
        role: 'user',
        text: text,
        timestamp: new Date(),
      };

      setMessages((prev) => {
        const updatedMessages = [...prev, userMessage];
        if (sessionId) {
          saveMessages(sessionId, updatedMessages);
        }
        return updatedMessages;
      });

      // Determine if we're in pure text mode (chat without active voice)
      const isPureTextMode = mode === 'chat' && !isVoiceInChat;
      console.warn('[VoiceWidgetConnected] handleSendMessage mode check:', {
        mode,
        isVoiceInChat,
        isPureTextMode,
      });

      // Path 1: Pure text mode - ALWAYS use WebSocket/HTTP API (bypass LiveKit)
      // This ensures text chat works independently of LiveKit connection
      // Note: We don't check isHttpApiAvailable here because we'll connect WebSocket if needed
      if (isPureTextMode) {
        // Issue #572: Queue message if previous response still streaming.
        // Backend serializes via session lock; frontend must match to avoid
        // messageId filtering from dropping intermediate responses.
        if (streamingMessageRef.current) {
          console.log(
            '[VoiceWidgetConnected] Queuing message (stream active):',
            text.substring(0, 50)
          );
          pendingTextMessagesRef.current.push(text);
          return;
        }

        console.warn(
          '[VoiceWidgetConnected] Sending via WebSocket (pure text mode)'
        );
        // Issue #587: Diagnostic log for duplicate request investigation
        console.warn(
          '[VoiceWidgetConnected][#587-DIAG] handleSendMessage sending user text:',
          {
            textLength: text.length,
            textPreview: text.substring(0, 50),
            timestamp: Date.now(),
            source: 'handleSendMessage',
          }
        );
        // Create placeholder for assistant response
        const assistantMsgId = generateMessageId('assistant');
        streamingMessageRef.current = assistantMsgId;

        const assistantMessage = {
          id: assistantMsgId,
          role: 'assistant',
          text: '',
          timestamp: new Date(),
          isStreaming: true,
        };

        setMessages((prev) => [...prev, assistantMessage]);

        // Connect to browser control WebSocket if not already
        if (!browserControlWS.isConnected()) {
          const sid = sessionId || getOrCreateSessionId();
          const wsUrl = getWsUrl();
          console.warn('[VoiceWidgetConnected] Connecting to WebSocket:', {
            wsUrl,
            sid,
          });
          try {
            await browserControlWS.connect(wsUrl, sid);

            // Verify connection was established (race condition fix)
            if (!browserControlWS.isConnected()) {
              throw new Error(
                'WebSocket not connected after connect() resolved'
              );
            }

            const { getCurrentPageContext } = await import(
              '../../utils/pageContextParser'
            );
            const pageContext = getCurrentPageContext();
            sendPageContextWithLaunch(
              pageContext,
              launchContext,
              clearLaunchContext
            );
            console.warn(
              '[VoiceWidgetConnected] WebSocket connected successfully'
            );
          } catch (wsError) {
            console.error(
              '[VoiceWidgetConnected] WebSocket connect error:',
              wsError
            );
            // Update UI with error and return early - don't try to send!
            setMessages((prev) => {
              const msgId = streamingMessageRef.current;
              if (!msgId) return prev;
              const idx = prev.findIndex((m) => m.id === msgId);
              if (idx === -1) return prev;
              const updated = [...prev];
              updated[idx] = {
                ...updated[idx],
                text: 'Failed to connect. Please try again.',
                error: true,
                isStreaming: false,
              };
              return updated;
            });
            streamingMessageRef.current = null;
            return;
          }
        } else {
          console.warn('[VoiceWidgetConnected] WebSocket already connected');
        }

        // Send message via WebSocket/HTTP API
        await sendHttpMessage(text);
        return;
      }

      // Path 2: Voice mode - use LiveKit DataChannel if connected
      const isWebRtcConnected =
        sessionState.status === 'connected' && sendMessageRef.current;
      if (isWebRtcConnected) {
        console.log(
          '[VoiceWidgetConnected] Sending via LiveKit DataChannel (voice mode)'
        );
        sendMessageRef.current(text);
        return;
      }

      // Path 3: Nothing available - save as pending and start LiveKit connection
      // This is fallback for voice mode when LiveKit is not yet connected
      console.log(
        '[VoiceWidgetConnected] No API available, saving as pending and connecting to LiveKit'
      );
      pendingMessageRef.current = text;
      await startConnection();
    },
    [
      sessionState.status,
      startConnection,
      sessionId,
      sendHttpMessage,
      mode,
      isVoiceInChat,
      launchContext,
      clearLaunchContext,
    ]
  );
  useEffect(() => {
    handleSendMessageRef.current = handleSendMessage;
  }, [handleSendMessage]);

  // Issue #752: Stable callback for suggestion pill click
  const handleSuggestionSelect = useCallback(
    (text, index) => {
      console.log('[Suggestions] clicked:', {
        index,
        text,
        messageId: latestAssistantMsgIdRef.current,
      });
      handleSendMessage(text);
    },
    [handleSendMessage]
  );

  // Sync sendMessage function with testing API
  useEffect(() => {
    setSendMessageFn(handleSendMessage);
  }, [handleSendMessage]);

  // Send pending message when connected
  // Note: Message is already in UI (added immediately in handleSendMessage)
  // This effect only sends it via LiveKit after connection is established
  useEffect(() => {
    if (sessionState.status === 'connected' && pendingMessageRef.current) {
      const pendingText = pendingMessageRef.current;
      pendingMessageRef.current = null;

      // Send via LiveKit (message already in UI)
      if (sendMessageRef.current) {
        sendMessageRef.current(pendingText);
      }
    }
  }, [sessionState.status]);

  // Hang up
  const handleHangup = useCallback(async () => {
    if (
      sessionState.status === 'disconnected' ||
      sessionState.status === 'idle'
    ) {
      return;
    }

    try {
      userInitiatedDisconnectRef.current = true; // Issue #593: Mark as user-initiated
      await controls.endCall();
    } catch (error) {
      console.error('[VoiceWidgetConnected] Failed to hang up:', error);
    }
  }, [controls, sessionState.status, sessionId]);

  // Close widget - ends any active voice session, then goes to collapsed
  const handleClose = useCallback(async () => {
    // End call in voice mode OR when there's an active voice session in any mode
    if (mode === 'voice' || sessionState.status === 'connected') {
      await handleHangup();
    }

    // If closing from voice mode and NOT coming from chat, sync position
    if (mode === 'voice' && previousMode !== 'chat') {
      saveWidgetGeometry('collapsed', voicePosition, null);
      console.log('[VoiceWidgetConnected] Synced voice position to collapsed');
    }

    // На мобилке: чат сдвигается вниз, потом mini
    // Таймаут чуть меньше CSS transition (0.3s) — переключение до конца анимации незаметно
    if (isMobile && mode === 'chat') {
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        setMode('collapsed');
        setIsMiniCollapsed(true);
        setWidgetState('normal');
      }, 280);
      return;
    }

    // Note: If previousMode === 'chat', positions will be reset to default by useEffect
    setMode('collapsed');
    if (isMobile) setIsMiniCollapsed(true);
    // Issue #496: Счётчик не сбрасываем при сворачивании - инкремент происходит при входе в text chat
    setWidgetState('normal'); // Reset maximized state when closing
  }, [
    mode,
    previousMode,
    sessionState.status,
    handleHangup,
    voicePosition,
    isMobile,
  ]);

  // Встроенный виджет (#1441): свернуть его нельзя — в макете осталась бы дыра,
  // — поэтому «закрыть» голос значит положить трубку и вернуться в чат того же
  // блока. Без этого у голосовой плашки в макете был мёртвый крестик, и после
  // звонка «À l'écoute…» висела, пока не перезагрузишь страницу.
  const inlineCloseInFlightRef = useRef(false);
  const pendingGenRef = useRef(null);
  const inlineRef = useRef(inline);
  useEffect(() => {
    inlineRef.current = inline;
  }, [inline]);
  const handleInlineVoiceClose = useCallback(async () => {
    // Второе закрытие, пока первое ещё кладёт трубку (клавиша отпустила
    // звонок, следом Enter), — не нужно: двойной endCall оставлял флаг
    // «пользователь положил трубку» на следующую сессию (#593).
    if (inlineCloseInFlightRef.current) return;
    inlineCloseInFlightRef.current = true;
    try {
      // Звонок мог ещё подниматься (микрофон, токен, поповер согласия):
      // отменяем и его.
      callGenRef.current += 1;
      setShouldConnect(false);
      setShowConsentPopover(false);
      pendingActionRef.current = null;
      // Звонок так и не вошёл в голос (снимка нет) — восстановления не будет,
      // и флаг сбрасываем здесь, иначе он блокировал бы следующие звонки.
      if (callSnapshotRef.current == null) isolatedCallRef.current = false;
      await handleHangup();
      // Ушли со страницы, пока клали трубку: виджет уже не встроенный и
      // свернулся сам — «чат» здесь раскрыл бы плавающее окно.
      if (inlineRef.current) setMode('chat');
    } finally {
      inlineCloseInFlightRef.current = false;
    }
  }, [handleHangup]);
  useEffect(() => {
    closeInlineVoiceRef.current = handleInlineVoiceClose;
  }, [handleInlineVoiceClose]);

  // Звонок с карточки без связи дольше 20 с — кончаем его (#1441, ревью !1301).
  // Повтор после обрыва может повиснуть в подключении, не вызвав больше ни
  // одного `onDisconnected`, и карточка тикала бы «в разговоре» с пустой
  // линией. Счёт идёт заново каждый раз, когда связь пропадает; только для
  // изолированного звонка — дилерский голос живёт по своим правилам.
  const isVoiceLinkUp = sessionState.status === 'connected';
  useEffect(() => {
    if (!isolatedCall || isVoiceLinkUp) return undefined;
    const id = setTimeout(() => {
      console.warn('[VoiceWidgetConnected] isolated call: no link for 20s — ending the call');
      closeInlineVoiceRef.current?.();
    }, 20000);
    return () => clearTimeout(id);
  }, [isolatedCall, isVoiceLinkUp]);

  // `closeVoiceAssistant()` из контекста теперь доходит до виджета: карточка
  // звонка кладёт трубку по-настоящему, а не только у себя на экране.
  useEffect(() => {
    setCloseHandler?.(inline ? handleInlineVoiceClose : handleClose);
    return () => setCloseHandler?.(null);
  }, [setCloseHandler, inline, handleInlineVoiceClose, handleClose]);

  // Maximize chat to full screen (mobile only)
  const handleMaximize = useCallback(() => {
    setWidgetState('maximized');
  }, []);

  // Restore chat from maximized to normal
  const handleRestore = useCallback(() => {
    setWidgetState('normal');
  }, []);

  // Collapse chat to voice/collapsed mode (minimize button in header)
  const handleCollapseToVoice = useCallback(() => {
    setWidgetState('normal'); // Reset maximized state when collapsing
    // Issue #496: Счётчик не сбрасываем при сворачивании - инкремент происходит при входе в text chat
    if (isVoiceInChat) {
      // If in voice-in-chat mode, collapse to voice mode (continue voice session)
      // Note: If previousMode === 'chat', position will be reset to default by useEffect
      setMode('voice');
    } else {
      // If in text chat mode, collapse to collapsed mode
      // Sync chat position to collapsed if NOT coming from chat originally
      if (mode === 'chat' && previousMode === 'voice') {
        saveWidgetGeometry('collapsed', voicePosition, null);
        console.log(
          '[VoiceWidgetConnected] Synced voice position to collapsed'
        );
      }
      // Note: If previousMode === 'chat', position will be reset to default by useEffect
      setMode('collapsed');
      // На мобилке при закрытии возвращаемся в mini-collapsed
      if (isMobile) setIsMiniCollapsed(true);
    }
  }, [isVoiceInChat, mode, previousMode, voicePosition, isMobile]);

  // Clear history
  const handleClearHistory = useCallback(() => {
    setMessages([]);
    if (sessionId) {
      saveMessages(sessionId, []);
    }
    clearSession();
    const newSessionId = getOrCreateSessionId();
    setSessionId(newSessionId);
  }, [sessionId]);

  // Room connected handler
  // NOTE: LiveKitRoom's onConnected is () => void — no room argument is passed.
  // Issue #735: Microphone permission is pre-requested in startConnection(),
  // then SDK auto-enables mic via <LiveKitRoom audio> prop (permission is already cached).
  // Metadata/attributes are set by LiveKitSession useEffect (useRoomContext).
  const handleRoomConnected = useCallback(() => {
    console.warn(
      '[566-DEBUG] handleRoomConnected — voiceRetryCount:',
      voiceRetryCountRef.current
    );

    // Issue #566: Reset retry counter on successful connection
    voiceRetryCountRef.current = 0;

    if (sessionId) {
      refreshSession();
    }
  }, [sessionId]);

  // Issue #593: Agent left the room (crash/restart scenario) — detected via useRemoteParticipants in LiveKitSession.
  // We do NOT set userInitiatedDisconnectRef here, so handleRoomDisconnected treats it as agent disconnect → no retry.
  const handleAgentDisconnected = useCallback(() => {
    console.warn(
      '[593] handleAgentDisconnected: agent left the room, disconnecting'
    );
    controls.endCall();
  }, [controls]);

  // Room disconnected handler
  const MAX_VOICE_RETRIES = 3;
  const VOICE_RETRY_DELAY_MS = 50; // Minimal delay for React state flush before retry

  const handleRoomDisconnected = useCallback(
    (reason) => {
      // Issue #566: Guard against cascade from LiveKitRoom unmount during retry.
      // When roomKey changes, React unmounts old LiveKitRoom which fires onDisconnected again.
      if (isRetryingRef.current) {
        console.warn(
          '[566-DEBUG] handleRoomDisconnected: SKIP (retry in progress)'
        );
        return;
      }

      // Issue #593: Guard against double disconnect — server ROOM_DELETED fires handleRoomDisconnected,
      // then Mechanism A detects agent left → endCall() → room.disconnect() → fires AGAIN.
      // On second call, refs are already reset by resetState → falls through to retry → zombie session.
      if (disconnectHandledRef.current) {
        console.warn('[593] handleRoomDisconnected: SKIP (already handled)');
        return;
      }

      // Issue #593: Detect agent-initiated disconnect (agent hang_up → delete_room).
      // DisconnectReason is unreliable (always CLIENT_INITIATED=1), so we use two signals:
      // 1. userInitiatedDisconnectRef — set to true ONLY when user explicitly clicks hang up/close
      // 2. hadActiveSessionRef — true if agent was connected at some point in this session
      //
      // Logic: if NOT user-initiated AND agent WAS active → agent disconnected → no retry.
      // If NOT user-initiated AND agent was NEVER active → peer connection failure → allow retry.
      const isUserDisconnect = userInitiatedDisconnectRef.current;
      const hadActiveSession = hadActiveSessionRef.current;
      userInitiatedDisconnectRef.current = false; // Reset flag for next session

      if (!isUserDisconnect && hadActiveSession) {
        // Agent/server initiated disconnect — agent was active, now gone → conversation ended
        console.warn(
          '[593] handleRoomDisconnected: agent disconnect detected (not user-initiated, had active session) — skipping retry',
          { reason }
        );
        disconnectHandledRef.current = true; // Issue #593: Prevent double handling from Mechanism A cascade
        voiceRetryCountRef.current = 0;
        const isTextChatMode = mode === 'chat';
        // WS принадлежит чату; изолированный звонок его не открывал (#1441).
        resetState(isTextChatMode || isolatedCallRef.current);
        // Collapse voice bar so user sees the widget return to idle state
        if (!isTextChatMode) {
          setMode('collapsed');
        }
        return;
      }

      console.warn('[566-DEBUG] handleRoomDisconnected ENTER', {
        mode,
        reason,
        voiceRetryCount: voiceRetryCountRef.current,
        MAX_VOICE_RETRIES,
        shouldConnect,
        isInitializing,
        connectionError,
      });

      // Issue #496: When switching from voice to text mode, keep WebSocket connected
      // because it's needed for text chat functionality
      const isTextChatMode = mode === 'chat';

      // Issue #566: Auto-retry for voice mode when connection drops unexpectedly
      // This handles the case when LiveKit peer connection fails after navigation
      const isVoiceMode = mode === 'voice';
      const canRetry =
        isVoiceMode && voiceRetryCountRef.current < MAX_VOICE_RETRIES;

      if (canRetry) {
        voiceRetryCountRef.current += 1;
        const attempt = voiceRetryCountRef.current;

        // Issue #566: Always get fresh connection details on retry.
        // Reusing the same token reconnects the participant to the same room,
        // but the LiveKit agent won't re-dispatch without a new room creation.
        // The HTTP round-trip is necessary for: new room → new agent dispatch.
        console.warn(
          `[566-DEBUG] handleRoomDisconnected: retry ${attempt} of ${MAX_VOICE_RETRIES}`
        );
        isRetryingRef.current = true;
        resetState(true); // keepWebSocket=true, clearDetails=true — forces new connection details

        // Ждём завершения CSS transition (0.5s)
        voiceRetryTimerRef.current = setTimeout(() => {
          isRetryingRef.current = false;
          // Guard: if user switched mode during retry delay, don't force back to voice
          if (modeRef.current !== 'voice') {
            console.warn(
              `[566-DEBUG] handleRoomDisconnected: mode changed to '${modeRef.current}', skipping retry`
            );
            return;
          }
          if (startConnectionRef.current) {
            startConnectionRef.current();
          }
          console.warn(
            `[566-DEBUG] handleRoomDisconnected: retry ${attempt} — startConnection()`
          );
        }, VOICE_RETRY_DELAY_MS);
        return;
      }

      console.warn(
        '[566-DEBUG] handleRoomDisconnected: NO MORE RETRIES, giving up. isTextChatMode:',
        isTextChatMode
      );
      // No more retries or not voice mode — full disconnect (clearDetails=true by default)
      resetState(isTextChatMode || isolatedCallRef.current);

      // Изолированный звонок (#1441): агент так и не подключился — звонок
      // кончается и чат возвращается к переписке до звонка. Системную строку
      // и сохранение пропускаем: `sessionId` здесь — сессия ЧАТА, и в её
      // историю легла бы расшифровка звонка.
      if (isolatedCallRef.current) {
        setMode('chat');
        voiceRetryCountRef.current = 0;
        return;
      }

      // Issue #496: Don't show "Conversation ended" when switching to text mode
      // - the conversation continues in text form
      if (!isTextChatMode) {
        const systemMessage = {
          id: generateMessageId('system-disconnect'),
          role: 'system',
          text: 'Conversation ended',
          timestamp: new Date(),
        };

        setMessages((prev) => {
          const updatedMessages = [...prev, systemMessage];
          if (sessionId) {
            saveMessages(sessionId, updatedMessages);
          }
          return updatedMessages;
        });
      }

      // Reset retry counter after giving up
      voiceRetryCountRef.current = 0;
    },
    [resetState, sessionId, mode]
  );

  // Late-bound handle on `openChat` (defined below). #1353: a text-only dealer
  // degrades a voice request to the text chat, and naming `openChat` in
  // handleMicClick's dependency array would be a TDZ/circular reference —
  // openChat itself depends on handleMicClick. Same ref pattern as
  // `sendHttpMessageRef` above.
  const openChatRef = useRef(null);

  // Consent popover handlers
  const handleConsentConfirm = useCallback(async () => {
    localStorage.setItem('widget_consentGiven', 'true');
    setConsentGiven(true);
    setShowConsentPopover(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    // Выполняем отложенное действие после подтверждения
    if (action === 'voice' && !voiceEnabled) {
      // #1353: сюда сейчас не дойти — единственный писатель pendingActionRef
      // ('voice') стоит за тем же гейтом в handleMicClick. Оставлено защитой
      // на будущих писателей, и защита именно ОТКРЫВАЕТ чат: пользователь
      // только что дал согласие, промолчать в ответ — худший из вариантов.
      openChatRef.current?.();
    } else if (action === 'voice') {
      // Issue #735: Request mic permission after consent, before starting session
      const gen = pendingGenRef.current ?? callGenRef.current;
      pendingGenRef.current = null;
      if (callGenRef.current !== gen) {
        isolatedCallRef.current = false;
        return;
      }
      await requestMicPermission();
      if (callGenRef.current !== gen) {
        isolatedCallRef.current = false;
        return;
      }
      // Дублируем логику handleMicClick напрямую чтобы избежать stale closure
      voiceRetryCountRef.current = 0;
      isRetryingRef.current = false;
      // Звонок с карточки (#1441) — тот же снимок переписки, что в handleMicClick.
      if (isolatedCallRef.current) {
        if (callSnapshotRef.current == null) callSnapshotRef.current = messagesRef.current;
        setIsolatedCall(true);
      }
      saveWidgetGeometry('voice', collapsedPosition, null);
      setMode('voice');
      setSpeakerMuted(false);
      startConnection();
    }
    // chat action будет обработан отдельно (пользователь опишет позже)
  }, [collapsedPosition, startConnection, requestMicPermission, voiceEnabled]);

  const handleConsentClose = useCallback(() => {
    setShowConsentPopover(false);
    pendingActionRef.current = null;
    // Отказались от звонка — следующий голос обычный, не изолированный.
    isolatedCallRef.current = false;
  }, []);

  // Mode change handlers
  const handleMicClick = useCallback(async (opts) => {
    // Флаг изоляции ставим ДО гейта согласия: подтверждение из поповера
    // (`handleConsentConfirm`) читает его и делает тот же снимок переписки.
    // Новый звонок, пока прежний ещё закрывается (двойной клик по трубке —
    // «положить» и «позвонить» стоят на одном месте), игнорируем: снимок
    // взялся бы с расшифровки прежнего звонка и лёг бы в историю чата
    // (ревью !1301).
    if (opts?.isolated === true && (isolatedCallRef.current || inlineCloseInFlightRef.current)) {
      // `false` — звонок не начат: карточка снимет «вызов» (ревью !1301).
      return false;
    }
    if (opts?.isolated === true) isolatedCallRef.current = true;
    const gen = callGenRef.current;
    const now = new Date();
    const timestamp = now.toISOString();
    console.log(`click voice conversation button: ${timestamp}`);

    // Issue #1353: у text-only дилера кнопки микрофона нет, но сюда ещё ведут
    // программные пути (SDK `open('voice')`, Sparkles, context shim). Не молчим
    // — открываем текстовый чат: для вызывающего "открой виджет" важнее, чем
    // именно "голосом".
    if (!voiceEnabled) {
      console.log(
        '[VoiceWidgetConnected] voice disabled for this dealer (#1353) → opening text chat'
      );
      isolatedCallRef.current = false;
      return openChatRef.current?.();
    }

    // Issue #658: показываем consent popover при первом клике в сессии.
    // Хранилище перечитываем в момент нажатия, а не верим state с монтирования:
    // согласие мог записать не сам виджет — карточка звонка на витрине кладёт
    // его перед стартом (#1441). Без этого новый посетитель получал поповер
    // внутри скрытого на время звонка контейнера, и звонок не начинался.
    const storedConsent = localStorage.getItem('widget_consentGiven') === 'true';
    if (!consentGiven && !storedConsent) {
      pendingActionRef.current = 'voice';
      // Поколение на момент нажатия: трубку положили, пока висел поповер, —
      // подтверждение звонок уже не поднимет (ревью !1301).
      pendingGenRef.current = gen;
      setShowConsentPopover(true);
      return;
    }
    if (!consentGiven) setConsentGiven(true);

    // Issue #735: Request mic permission before starting voice session.
    // Resolved before room creation so agent greeting is not lost.
    await requestMicPermission();
    // Пока ждали микрофон, трубку положили — звонок не поднимаем (#1441).
    if (callGenRef.current !== gen) {
      isolatedCallRef.current = false;
      return;
    }

    // Issue #566: Reset retry state on manual connection start
    voiceRetryCountRef.current = 0;
    isRetryingRef.current = false;

    // Изолированный звонок (#1441): переписку чата замораживаем снимком —
    // реплики разговора в неё попадут, но после звонка она вернётся как была.
    if (isolatedCallRef.current) {
      // Снимок — один раз за звонок: повторный вход сюда не должен подменить
      // переписку до звонка расшифровкой.
      if (callSnapshotRef.current == null) callSnapshotRef.current = messagesRef.current;
      setIsolatedCall(true);
    }

    // Save current collapsed position as voice position (prevent jumping)
    saveWidgetGeometry('voice', collapsedPosition, null);
    setMode('voice');
    // Enable speaker for voice mode
    setSpeakerMuted(false);
    startConnection();
  }, [
    startConnection,
    collapsedPosition,
    consentGiven,
    requestMicPermission,
    voiceEnabled,
  ]);

  /**
   * Issue #944: Unified chat-open flow. Single source of truth for opening the
   * chat from any entry point (UI button, SDK CustomEvent, React Context
   * shim, Sparkles, header link). Runs the full text init — history,
   * useTextChat reinit, WS connect, mic mute, auto-greeting — and routes
   * `mode === 'voice'` to the existing voice path so callers don't have to
   * branch on mode themselves.
   *
   * @param {Object} opts
   * @param {'chat' | 'text' | 'voice'} [opts.mode='chat'] — 'text' is an alias for 'chat'.
   * @param {string} [opts.action] — typed action name (registered in backend
   *   ACTION_REGISTRY); defaults to ``widget_opened`` when no explicit
   *   launchContext is provided.
   * @param {Object} [opts.metadata] — action-specific metadata.
   * @param {string} [opts.targetPage] — used by the internal ``page_navigated`` action.
   */
  const openChat = useCallback(
    async (opts = {}) => {
      const { mode: targetMode = 'chat', action, metadata, targetPage } = opts;
      const normalizedMode = targetMode === 'text' ? 'chat' : targetMode;

      // Voice mode → existing voice entry point. Text-only dealers (#1353) fall
      // through to the chat path below instead (handleMicClick would bounce
      // straight back here).
      if (normalizedMode === 'voice' && voiceEnabled) {
        return handleMicClick({ isolated: opts.isolated === true });
      }

      // Already in chat: keep the legacy no-op behaviour (avoid duplicate
      // greeting bubble / WS churn). The new launchContext is intentionally
      // dropped here; surfacing it mid-conversation is a UX call for a follow-up.
      if (mode === 'chat') {
        console.log(
          '[VoiceWidgetConnected] openChat: already in chat, skipping reinit (action=%s)',
          action ?? '(none)'
        );
        return undefined;
      }

      const timestamp = new Date().toISOString();
      console.log(
        `[VoiceWidgetConnected] openChat: ${timestamp} action=${
          action ?? 'widget_opened'
        }`
      );

      // Build effective launch context. Caller-provided action/metadata wins;
      // otherwise fall back to (1) the synchronous SDK ref (Issue #568), then
      // (2) the React Context launchContext, then (3) the implicit
      // ``widget_opened`` action so the agent always greets us.
      let effectiveLaunchContext;
      if (action || metadata || targetPage) {
        effectiveLaunchContext = { action, metadata, targetPage };
      } else {
        effectiveLaunchContext = sdkLaunchContextRef.current ||
          launchContext || { action: 'widget_opened' };
      }
      sdkLaunchContextRef.current = null; // one-time use (Issue #568)

      // #1362: язык витрины сюда НЕ проставляем — это делает транспорт
      // (`useTextChat` → `browserControlWS.sendChatMessage`). Штамп здесь покрыл
      // бы только этот вход и мимо него всё равно проходят form→chat handover и
      // авто-старт после навигации.

      // Открытие чата = пользователь увидит нотис в чат-баннере — считаем согласие полученным
      if (!consentGiven) {
        localStorage.setItem('widget_consentGiven', 'true');
        setConsentGiven(true);
      }

      // При открытии chat из collapsed, устанавливаем дефолтную позицию (правый нижний угол)
      // На мобилке позиция управляется CSS (bottom sheet), поэтому не сохраняем
      if (mode === 'collapsed' && !isMobile) {
        const defaultChatPosition = {
          x: window.innerWidth - (savedGeometryChat.size?.width || 450) - 24,
          y: window.innerHeight - (savedGeometryChat.size?.height || 600) - 24,
        };
        saveWidgetGeometry('chat', defaultChatPosition, savedGeometryChat.size);
      }
      setMode('chat');

      // Load messages from localStorage when opening chat
      const sid = sessionId || getOrCreateSessionId();
      const mappedMessages = loadAndMapMessages(sid);
      if (mappedMessages.length > 0) {
        // #1485: keep an answer b2b-api is still handing over after a reload; it is stored
        // once it finishes.
        setMessages((prev) =>
          keepInFlight(mappedMessages, prev, proactiveMessageRef.current)
        );
      }

      // Issue #496: Активируем текстовый чат для переинициализации useTextChat
      setTextChatVersion((v) => v + 1);
      // Chat mode = text only, disable voice
      setIsVoiceInChat(false);
      setSpeakerMuted(true);
      // Disable microphone for text-only mode
      if (sessionState.status === 'connected') {
        try {
          await controls.toggleMute(false); // false = mute
        } catch (error) {
          console.error(
            '[VoiceWidgetConnected] Failed to mute microphone:',
            error
          );
        }
      }
      // Text chat mode - connect to WebSocket only (no LiveKit).
      // LiveKit will be connected when user switches to voice mode.
      // Update React state so useTextChat hook has the correct sessionId
      if (!sessionId) {
        setSessionId(sid);
      }
      if (!browserControlWS.isConnected()) {
        try {
          await browserControlWS.connect(getWsUrl(), sid);
          console.log(
            '[VoiceWidgetConnected] Connected to WebSocket for text chat'
          );

          // Verify connection was established (race condition fix)
          if (!browserControlWS.isConnected()) {
            console.error(
              '[VoiceWidgetConnected] WebSocket not connected after connect() resolved'
            );
            return undefined; // Exit early - cannot send auto-greeting without connection
          }
        } catch (wsError) {
          console.error(
            '[VoiceWidgetConnected] WebSocket connect failed:',
            wsError
          );
          return undefined;
        }
      }

      // Issue #577: Track conversation start for text mode (once per session)
      if (!conversationTrackedRef.current) {
        conversationTrackedRef.current = true;
        trackConversationStart({ mode: 'text' });
      }

      // Issue #461: Auto-greeting — пустое сообщение с launchContext триггерит приветствие от агента.
      if (effectiveLaunchContext) {
        // Issue #587: Diagnostic log for duplicate request investigation
        console.warn(
          '[VoiceWidgetConnected][#587-DIAG] openChat sending launch_context:',
          {
            action: effectiveLaunchContext.action,
            targetPage: effectiveLaunchContext.targetPage,
            timestamp: Date.now(),
            source: 'openChat',
            currentMode: mode,
          }
        );
        console.log(
          '[VoiceWidgetConnected] Triggering auto-greeting with launchContext:',
          effectiveLaunchContext
        );

        // Создаём placeholder для ответа ассистента
        const assistantMsgId = generateMessageId('greeting');
        streamingMessageRef.current = assistantMsgId;

        const assistantMessage = {
          id: assistantMsgId,
          role: 'assistant',
          text: '',
          timestamp: new Date(),
          isStreaming: true,
        };

        setMessages((prev) => [...prev, assistantMessage]);

        // Отправляем пустое сообщение с launchContext
        await sendHttpMessage('', { launchContext: effectiveLaunchContext });

        // Очищаем launchContext после отправки (только если был передан реальный)
        if (launchContext) {
          clearLaunchContext();
        }
      }

      return undefined;
    },
    [
      handleMicClick,
      mode,
      sessionState.status,
      controls,
      savedGeometryChat.size,
      sessionId,
      launchContext,
      clearLaunchContext,
      sendHttpMessage,
      consentGiven,
      isMobile,
      voiceEnabled,
    ]
  );

  /**
   * Native UI chat-bubble click — entry point #1 in the Issue #944 inventory.
   * Thin wrapper around {@link openChat}; the heavy lifting now lives in one
   * place so SDK CustomEvent and React Context callers converge on the same
   * init flow.
   */
  openChatRef.current = openChat;

  const handleChatClick = useCallback(() => openChat(), [openChat]);

  // Issue #944: Register openChat with VoiceAssistantContext so the React
  // Context's openChat() can fire the full text-init flow (history,
  // useTextChat reinit, WS, greeting) instead of leaving the widget in a
  // half-initialised state with a stray LiveKit connection.
  useEffect(() => {
    if (!setOpenChatHandler) return undefined;
    setOpenChatHandler(openChat);
    return () => setOpenChatHandler(null);
  }, [openChat, setOpenChatHandler]);

  // Issue #944 Slice 2b: Pre-warm the browser-control WebSocket at widget mount,
  // so opening the chat from ANY entry point (chat-bubble, SDK CustomEvent,
  // Sparkles, header link) doesn't pay the ~200–500 ms WS handshake cost on
  // click. Targets the design-doc <300 ms cold-greeting latency budget.
  //
  // The connect is idempotent (browserControlWebSocket.js:42 short-circuits
  // when `ws.readyState === OPEN`), so the click path's `if (!isConnected())`
  // simply skips the extra connect attempt. If the pre-warm fails (e.g. token
  // not ready yet), the click path retries — same fallback semantics as today.
  useEffect(() => {
    if (browserControlWS.isConnected()) return undefined;
    const wsUrl = getWsUrl();
    if (!wsUrl) return undefined; // Token not configured yet — click path will retry.
    const sid = getOrCreateSessionId();
    // #1485: the agent answers its navigation once the reloaded page's context arrives. A page
    // restored into chat or voice sends that context with its first request; one restored
    // collapsed sends no request, so it reports its page here, once, after registering.
    const reportsReloadedPage =
      isNavigationRestoreRef.current &&
      initialModeRef.current !== 'chat' &&
      initialModeRef.current !== 'voice';
    const registered = reportsReloadedPage
      ? browserControlWS.waitForContextAck('registered', null, 5000)
      : null;
    browserControlWS
      .connect(wsUrl, sid)
      .then(async () => {
        console.log(
          '[VoiceWidgetConnected] WS pre-warmed at mount (Issue #944 Slice 2b)'
        );
        if (registered) {
          await registered;
          const { getCurrentPageContext } = await import(
            '../../utils/pageContextParser'
          );
          browserControlWS.sendPageContext(getCurrentPageContext());
        }
      })
      .catch((err) => {
        // Silent (info-level) — actual click will retry through the same path.
        console.info(
          '[VoiceWidgetConnected] WS pre-warm failed (will retry on click):',
          err?.message || err
        );
      });
    // No cleanup: WS persists for widget lifetime; closing on unmount would
    // defeat the purpose if the user re-mounts (e.g., after route navigation
    // inside an SPA host page).
    // Empty deps: pre-warm runs exactly once at mount.
  }, []);

  // Expand from voice bar to chat with voice active (preserves voice session)
  const handleExpandToChat = useCallback(() => {
    // При расширении из voice, устанавливаем дефолтную позицию чата
    const defaultChatPosition = {
      x: window.innerWidth - (savedGeometryChat.size?.width || 450) - 24,
      y: window.innerHeight - (savedGeometryChat.size?.height || 600) - 24,
    };
    saveWidgetGeometry('chat', defaultChatPosition, savedGeometryChat.size);

    setMode('chat');
    // Issue #496: Активируем текстовый чат (даже при voice-in-chat можно отправлять текстовые сообщения)
    setTextChatVersion((v) => v + 1);
    // Voice session continues - enable voice-in-chat mode
    setIsVoiceInChat(true);
    // Keep speaker unmuted for voice
    setSpeakerMuted(false);
    // Microphone state is preserved from voice mode
  }, [savedGeometryChat.size]);

  const handleModeChange = useCallback(
    async (newMode) => {
      // Issue #1353: тумблер voice/chat у text-only дилера не рендерится, но
      // событие может прийти из SDK (`shiftgears:set-mode`) — игнорируем.
      if (newMode === 'voice' && !voiceEnabled) {
        console.log(
          '[VoiceWidgetConnected] handleModeChange: voice disabled for this dealer (#1353), ignoring'
        );
        return;
      }

      console.log('[VoiceWidgetConnected] handleModeChange:', {
        currentMode: mode,
        newMode,
        sessionStatus: sessionState.status,
        isVoiceInChat,
        hasConnectionDetails: !!connectionDetails,
        sessionId: sessionId?.substring(0, 8),
      });

      // Если мы в chat режиме widget'а, свитчер переключает voice-in-chat
      if (mode === 'chat') {
        if (newMode === 'voice') {
          // Включить voice в чате
          setIsVoiceInChat(true);
          setSpeakerMuted(false);

          // Issue #577: Track mode switch text→voice for analytics (backend + GTM)
          if (browserControlWS.isConnected()) {
            browserControlWS.sendUserAction('mode-change', {
              from: 'text',
              to: 'voice',
              reason: 'user_switched_mode',
            });
          }
          trackModeSwitch({ from: 'text', to: 'voice' });

          // Connect to LiveKit if not already connected
          if (sessionState.status !== 'connected') {
            console.log(
              '[VoiceWidgetConnected] Connecting to LiveKit for voice-in-chat, sessionId:',
              sessionId?.substring(0, 8)
            );
            setIsModeChanging(true); // Показываем индикатор загрузки
            try {
              await startConnection();
              // isModeChanging сбросится в useEffect когда status станет 'connected'
            } catch (error) {
              console.error('[VoiceWidgetConnected] Failed to connect:', error);
              setIsModeChanging(false);
              setIsVoiceInChat(false); // Откат если подключение не удалось
            }
          } else {
            // Already connected, just unmute
            try {
              await controls.toggleMute(true); // unmute
            } catch (error) {
              console.error(
                '[VoiceWidgetConnected] Failed to unmute microphone:',
                error
              );
            }
          }
        } else {
          // Выключить voice в чате (перейти в text режим)
          // ВАЖНО: Полностью отключаем LiveKit соединение чтобы агент не ждал ответа
          console.log(
            '[VoiceWidgetConnected] handleModeChange: switching from voice to text, disconnecting LiveKit'
          );
          setIsVoiceInChat(false);
          setSpeakerMuted(true);
          // Issue #496: Активируем текстовый чат для переинициализации useTextChat
          setTextChatVersion((v) => v + 1);

          // Отключаем LiveKit room полностью (не просто мутим микрофон)
          // Это нужно чтобы агент получил disconnect event и не спрашивал "вы ещё тут?"
          if (sessionState.status === 'connected') {
            try {
              userInitiatedDisconnectRef.current = true; // Issue #593: Mark as user-initiated
              await controls.endCall();
              console.log(
                '[VoiceWidgetConnected] LiveKit disconnected on mode change to text'
              );
            } catch (error) {
              console.error(
                '[VoiceWidgetConnected] Failed to disconnect LiveKit:',
                error
              );
            }
          }

          // Уведомляем агента о смене режима через WebSocket
          if (browserControlWS.isConnected()) {
            browserControlWS.sendUserAction('mode-change', {
              from: 'voice',
              to: 'text',
              reason: 'user_switched_mode',
            });
          }
          trackModeSwitch({ from: 'voice', to: 'text' });
        }
        return;
      }

      // В других режимах переключаем режим widget'а (не используется сейчас)
      setMode(newMode);

      if (newMode === 'voice') {
        // Voice mode = enable speaker and microphone
        setSpeakerMuted(false);
        if (sessionState.status === 'connected') {
          try {
            await controls.toggleMute(true); // true = unmute
          } catch (error) {
            console.error(
              '[VoiceWidgetConnected] Failed to unmute microphone:',
              error
            );
          }
        }
      } else if (newMode === 'chat') {
        // Chat mode = text only, disable voice
        setSpeakerMuted(true);
        if (sessionState.status === 'connected') {
          try {
            await controls.toggleMute(false); // false = mute
          } catch (error) {
            console.error(
              '[VoiceWidgetConnected] Failed to mute microphone:',
              error
            );
          }
        }
      }
    },
    [
      mode,
      sessionState.status,
      controls,
      startConnection,
      isVoiceInChat,
      connectionDetails,
      sessionId,
      voiceEnabled,
    ]
  );

  // SDK Event Listeners - allow programmatic control via ShiftGearsWidget SDK
  useEffect(() => {
    // Issue #944: Open widget in specified mode (voice/chat/text) via the
    // unified openChat dispatcher. Passes launchContext through explicitly so
    // the same code path serves SDK callers and React Context callers.
    const openInMode = (targetMode, launchCtx = null) => {
      const normalizedMode =
        targetMode === 'text' ? 'chat' : targetMode || 'chat';
      return openChat({
        mode: normalizedMode,
        action: launchCtx?.action,
        metadata: launchCtx?.metadata,
        targetPage: launchCtx?.targetPage,
      });
    };

    // Handle SDK open command with optional mode and launchContext (#568)
    const handleSdkOpen = (event) => {
      const { mode: targetMode, launchContext: sdkLaunchContext } =
        event.detail || {};
      // Issue #587: Diagnostic log for duplicate request investigation
      console.warn(
        '[VoiceWidgetConnected][#587-DIAG] SDK shiftgears:open received:',
        {
          targetMode: targetMode || 'chat',
          sdkLaunchContext: sdkLaunchContext || null,
          currentMode: mode,
          timestamp: Date.now(),
          source: 'SDK-handleSdkOpen',
        }
      );
      console.log(
        '[VoiceWidgetConnected] SDK open command received, mode:',
        targetMode || 'chat',
        'launchContext:',
        sdkLaunchContext || null
      );

      // Issue #568 legacy: store SDK launch context in ref so the synchronous
      // openChat default-fallback path (when called with no args) still finds
      // it. The Issue #944 path passes launchCtx through openInMode explicitly
      // instead, but keeping the ref preserves back-compat for any out-of-band
      // callers that read it.
      if (sdkLaunchContext) {
        sdkLaunchContextRef.current = sdkLaunchContext;
      }

      if (mode === 'collapsed') {
        openInMode(targetMode || 'chat', sdkLaunchContext);
      } else if (targetMode) {
        // Already open, but switch mode if specified
        const normalizedMode = targetMode === 'text' ? 'chat' : targetMode;
        if (normalizedMode !== mode) {
          handleModeChange(normalizedMode);
        }
      }
    };

    // Handle SDK close command
    const handleSdkClose = () => {
      console.log('[VoiceWidgetConnected] SDK close command received');
      if (mode !== 'collapsed') {
        handleClose();
      }
    };

    // Handle SDK toggle command with optional mode
    const handleSdkToggle = (event) => {
      const { mode: targetMode } = event.detail || {};
      console.log(
        '[VoiceWidgetConnected] SDK toggle command received, mode:',
        targetMode || 'default'
      );

      if (mode === 'collapsed') {
        openInMode(targetMode || 'chat');
      } else {
        handleClose();
      }
    };

    // Handle SDK set-mode command (switch mode without close/open)
    const handleSdkSetMode = (event) => {
      const { mode: targetMode } = event.detail || {};
      if (!targetMode) return;

      console.log(
        '[VoiceWidgetConnected] SDK set-mode command received:',
        targetMode
      );
      const normalizedMode = targetMode === 'text' ? 'chat' : targetMode;

      if (normalizedMode === 'collapsed') {
        handleClose();
      } else if (mode === 'collapsed') {
        openInMode(normalizedMode);
      } else if (normalizedMode !== mode) {
        handleModeChange(normalizedMode);
      }
    };

    // Handle SDK send message command
    const handleSdkSendMessage = (event) => {
      const { text } = event.detail || {};
      if (text) {
        console.log('[VoiceWidgetConnected] SDK send message command:', text);
        handleSendMessage(text);
      }
    };

    window.addEventListener('shiftgears:open', handleSdkOpen);
    window.addEventListener('shiftgears:close', handleSdkClose);
    window.addEventListener('shiftgears:toggle', handleSdkToggle);
    window.addEventListener('shiftgears:set-mode', handleSdkSetMode);
    window.addEventListener('shiftgears:send-message', handleSdkSendMessage);

    return () => {
      window.removeEventListener('shiftgears:open', handleSdkOpen);
      window.removeEventListener('shiftgears:close', handleSdkClose);
      window.removeEventListener('shiftgears:toggle', handleSdkToggle);
      window.removeEventListener('shiftgears:set-mode', handleSdkSetMode);
      window.removeEventListener(
        'shiftgears:send-message',
        handleSdkSendMessage
      );
    };
  }, [
    mode,
    openChat,
    handleMicClick,
    handleModeChange,
    handleClose,
    handleSendMessage,
  ]);

  const isConnected = sessionState.status === 'connected';

  // Issue #566: Compute actual connection status for VoiceBar
  // Instead of defaulting to 'connected', show real state
  const voiceConnectionStatus =
    isInitializing || isFetchingDetails
      ? 'connecting'
      : connectionError
      ? 'disconnected'
      : isConnected
      ? 'connected'
      : shouldConnect
      ? 'connecting'
      : 'disconnected';

  // [566-DEBUG] Log voiceConnectionStatus changes
  useEffect(() => {
    console.warn(
      '[566-DEBUG] voiceConnectionStatus changed:',
      voiceConnectionStatus,
      {
        isInitializing,
        isFetchingDetails,
        connectionError,
        isConnected,
        shouldConnect,
        sessionStatus: sessionState.status,
      }
    );
  }, [
    voiceConnectionStatus,
    isInitializing,
    isFetchingDetails,
    connectionError,
    isConnected,
    shouldConnect,
    sessionState.status,
  ]);

  // [566-DEBUG] Log LiveKitRoom render conditions
  useEffect(() => {
    console.warn('[566-DEBUG] LiveKitRoom render gate:', {
      hasConnectionDetails: !!connectionDetails,
      shouldConnect,
      roomKey,
      serverUrl: connectionDetails?.serverUrl,
      tokenPrefix: connectionDetails?.participantToken?.substring(0, 20),
    });
  }, [connectionDetails, shouldConnect, roomKey]);

  // Fixed positioning styles based on mode and device
  // Инлайн-чат не сворачивается: на его месте в потоке страницы осталась бы
  // пустая дыра. Сделано эффектом, а не обёрткой над `setMode` — обёртка
  // перестаёт быть стабильным setState-сеттером, и ESLint требует её в deps
  // десятка хуков (+10 предупреждений при храповике 186). Кнопки сворачивания
  // в инлайне нет, так что это страховка от не-UI путей (например, конец
  // сессии), а не основной механизм.
  useEffect(() => {
    if (inline && mode === 'collapsed') setMode('chat');
  }, [inline, mode]);

  // Обратный переход: ушли со страницы со встроенным чатом — виджет обязан
  // свернуться. Экземпляр один на всё приложение и навигацию переживает, а на
  // той странице ему форсируют режим `chat`; без этого он оставался раскрытым
  // плавающим окном на всех следующих страницах.
  const wasInline = useRef(inline);
  useEffect(() => {
    if (wasInline.current && !inline) setMode('collapsed');
    wasInline.current = inline;
  }, [inline]);

  // Звонок с карточки на витрине (#1441): голос идёт, а встроенный виджет
  // продолжает показывать чат — ту же переписку, что была до звонка. Режим
  // `voice` остаётся внутренним: от него зависят подключение и повторы, а
  // экран про него не знает. Реплики звонка в этот вид не попадают.
  const showChatDuringCall = inline && isolatedCall;
  const viewMode = showChatDuringCall ? 'chat' : mode;
  const viewMessages = showChatDuringCall ? callSnapshotRef.current || [] : messages;

  const getContainerStyle = () => {
    // Инлайн: блок занимает своё место в потоке, размеры задаёт контейнер.
    if (inline) {
      return { position: 'relative', width: '100%', height: '100%' };
    }

    // Mobile chat mode: bottom sheet - minimal styles, CSS handles the rest
    if (isMobile && mode === 'chat') {
      return {
        position: 'fixed',
        zIndex: 100,
        // left, bottom, width, height handled by CSS (.voice-widget-container--chat)
      };
    }

    // Mobile collapsed (mini + expanded): CSS-центрирование, без drag
    if (isMobile && mode === 'collapsed') {
      return {
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: '16px',
        zIndex: 100,
      };
    }

    // Desktop collapsed: draggable position
    if (mode === 'collapsed') {
      return {
        position: 'fixed',
        left: `${collapsedPosition.x}px`,
        top: `${collapsedPosition.y}px`,
        zIndex: 100,
        cursor: isDraggingCollapsed ? 'grabbing' : 'grab',
      };
    }

    // Mobile voice: CSS-центрирование (как collapsed) для плавного перехода
    if (isMobile && mode === 'voice') {
      return {
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: '16px',
        zIndex: 100,
      };
    }

    if (mode === 'voice') {
      return {
        position: 'fixed',
        left: `${voicePosition.x}px`,
        top: `${voicePosition.y}px`,
        zIndex: 100,
        cursor: isDraggingVoice ? 'grabbing' : 'grab',
      };
    }

    // Chat mode - Desktop: draggable/resizable
    return {
      position: 'fixed',
      left: `${chatPosition.x}px`,
      top: `${chatPosition.y}px`,
      zIndex: 100,
      width: `${size.width}px`,
      height: `${size.height}px`,
      cursor: isDraggingChat ? 'grabbing' : 'grab',
    };
  };

  // Merge container positioning with theme CSS variables so that
  // elements outside VoiceWidget (e.g. ConsentPopover) inherit --widget-primary
  const themeVars = getThemeStyles(theme);
  // #1232 item 3 — hide the floating widget while a config-driven form modal is
  // open so it doesn't cover the full-screen form / its submit on mobile. State
  // is preserved (display:none, not unmount) so it reappears on close.
  const containerStyle = {
    ...getContainerStyle(),
    ...themeVars,
    ...(formModalOpen ? { display: 'none' } : null),
  };

  // Should show resize handles? (chat and voice modes, desktop only)
  const showResizeHandles =
    !inline && !isMobile && (mode === 'chat' || mode === 'voice');

  // Get appropriate drag handler based on mode (mouse events)
  const getDragHandler = () => {
    if (inline) return undefined;
    if (mode === 'collapsed') return handleCollapsedDrag;
    if (mode === 'voice') return handleVoiceDrag;
    if (mode === 'chat' && !isMobile) return handleChatDrag; // Chat drag only on desktop
    return undefined;
  };

  // Get appropriate touch handler based on mode (touch events for mobile)
  const getTouchHandler = () => {
    if (inline) return undefined;
    if (mode === 'collapsed')
      return isMobile ? undefined : handleCollapsedTouchStart;
    if (mode === 'voice') return handleVoiceTouchStart;
    if (mode === 'chat' && !isMobile) return handleChatTouchStart; // Chat drag only on desktop
    return undefined;
  };

  // Check if any mode is dragging
  const isDragging = isDraggingCollapsed || isDraggingVoice || isDraggingChat;

  // Wrapper for resize handles - in voice mode, switch to chat on resize start
  const handleResizeMouseDown = useCallback(
    (originalHandler) => (e) => {
      if (mode === 'voice') {
        // Calculate chat size (use saved or defaults)
        const chatWidth = savedGeometryChat.size?.width || 450;
        const chatHeight = savedGeometryChat.size?.height || 600;

        // Start from current voice position
        let newX = voicePosition.x;
        let newY = voicePosition.y;

        // Adjust position to ensure chat is visible on screen
        // Check right edge
        if (newX + chatWidth > window.innerWidth) {
          newX = window.innerWidth - chatWidth - 24; // 24px margin
        }
        // Check bottom edge (most important - don't open below viewport)
        if (newY + chatHeight > window.innerHeight) {
          newY = window.innerHeight - chatHeight - 24; // 24px margin
        }
        // Check left edge
        if (newX < 24) {
          newX = 24;
        }
        // Check top edge
        if (newY < 24) {
          newY = 24;
        }

        // Save adjusted position as chat position
        saveWidgetGeometry(
          'chat',
          { x: newX, y: newY },
          savedGeometryChat.size
        );
        // Switch to chat mode
        setMode('chat');
        // Don't start resize immediately - will start in chat mode after re-render
        return;
      }
      // In chat mode, proceed with resize
      originalHandler(e);
    },
    [mode, voicePosition, savedGeometryChat.size]
  );

  const widgetTree = (
    <>
      <div
        style={containerStyle}
        className={`voice-widget-container${
          isClosing ? ' voice-widget-container--closing' : ''
        } voice-widget-container--${viewMode} ${
          widgetState === 'maximized' ? 'voice-widget-container--maximized' : ''
        } ${
          isDragging || isResizing ? 'voice-widget-container--interacting' : ''
        }`}
        onMouseDown={getDragHandler()}
        onTouchStart={getTouchHandler()}
      >
        {/* Resize handles - for desktop chat and voice modes */}
        {showResizeHandles &&
          resizeHandles.map((handle) => (
            <div
              key={handle.position}
              className={`resize-handle resize-handle--${handle.position}`}
              style={{ cursor: handle.cursor }}
              onMouseDown={handleResizeMouseDown(handle.onMouseDown)}
            />
          ))}
        {/* Issue #658: Consent popover - показываем при первом клике на voice/chat */}
        {showConsentPopover && mode === 'collapsed' && (
          <ConsentPopover
            onConfirm={handleConsentConfirm}
            onClose={handleConsentClose}
            termsUrl={termsUrl}
            privacyUrl={privacyUrl}
          />
        )}
        <VoiceWidget
          language={language}
          // Строки интерфейса из #1431 — на языке витрины только на своём
          // стоке: дилерский лендинг по `?token` тоже получает `language` из
          // App, и там они остаются английскими, как до #1431.
          chromeLanguage={isOwnStock ? language : 'en'}
          mode={viewMode}
          isSpeaking={sessionState.isSpeaking && !speakerMuted}
          isMuted={sessionState.isMuted}
          speakerMuted={speakerMuted}
          isSessionActive={isConnected}
          connectionStatus={voiceConnectionStatus}
          messages={viewMessages}
          title={effectiveTitle}
          showWhatsApp={effectiveShowWhatsApp}
          whatsAppNumber={effectiveWhatsAppNumber}
          showTelegram={effectiveShowTelegram}
          telegramHandle={effectiveTelegramHandle}
          errorMessage={connectionError || ''}
          theme={theme}
          onMicClick={handleMicClick}
          onChatClick={handleChatClick}
          onModeChange={handleModeChange}
          onSendMessage={handleSendMessage}
          onMicToggle={handleMuteToggle}
          onSpeakerToggle={toggleSpeakerMute}
          // Встроенный в страницу чат не закрывают и не сворачивают: под ним
          // не плавающее окно, а блок вёрстки — свернувшись, он оставил бы
          // дыру. Обработчиков нет, и шапка сама не рисует кнопки.
          // Исключение — голос: его крестик кладёт трубку и возвращает чат
          // (#1441), иначе плашка «À l'écoute…» оставалась без выхода.
          onClose={
            inline ? (viewMode === 'voice' ? handleInlineVoiceClose : undefined) : handleClose
          }
          onCollapseToVoice={inline ? undefined : handleCollapseToVoice}
          onExpandToChat={handleExpandToChat}
          // Mini collapsed (mobile only)
          isMini={isMobile && isMiniCollapsed && mode === 'collapsed'}
          onExpand={() => setIsMiniCollapsed(false)}
          // Window state (mobile maximize/restore via swipe)
          widgetState={widgetState}
          onMaximize={handleMaximize}
          onRestore={handleRestore}
          // Voice in chat
          showVoiceControls={isVoiceInChat}
          onVoiceClose={handleVoiceClose}
          isUserSpeaking={isUserSpeaking}
          userAudioLevel={userAudioLevel}
          agentAudioLevel={agentAudioLevel}
          isMobile={isMobile}
          // #1353 — text-only dealer: no mic in the collapsed bar, no
          // voice/chat toggle in the chat header.
          voiceEnabled={voiceEnabled}
          // Mode switching indicator
          isModeChanging={isModeChanging}
          // Consent banner in chat
          showConsentBanner={true}
          // Legal URLs
          termsUrl={termsUrl}
          privacyUrl={privacyUrl}
          // Issue #752: Auto-suggestions
          suggestions={config.SUGGESTIONS_ENABLED ? suggestions : []}
          suggestionsLoading={
            config.SUGGESTIONS_ENABLED ? suggestionsLoading : false
          }
          suggestionsConfig={{
            enabled: config.SUGGESTIONS_ENABLED,
            maxVisible: config.SUGGESTIONS_MAX_VISIBLE,
            accentColor: config.SUGGESTIONS_ACCENT_COLOR,
            variant: config.SUGGESTIONS_BUBBLE_VARIANT,
          }}
          onSuggestionSelect={handleSuggestionSelect}
        />
      </div>

      {connectionDetails && (
        <LiveKitRoom
          key={roomKey}
          serverUrl={connectionDetails.serverUrl}
          token={connectionDetails.participantToken}
          connect={shouldConnect}
          audio
          video={false}
          onConnected={handleRoomConnected}
          onDisconnected={handleRoomDisconnected}
        >
          <RoomAudioRenderer muted={speakerMuted} />
          <StartAudio label="Start Audio" />
          <LiveKitSession
            onSessionState={setSessionState}
            onMessagesChange={handleMessagesChange}
            onControlsReady={setControls}
            onSendMessageReady={(sendFn) => {
              sendMessageRef.current = sendFn;
            }}
            onUserSpeakingChange={setIsUserSpeaking}
            onUserAudioLevelChange={setUserAudioLevel}
            onAgentAudioLevelChange={setAgentAudioLevel}
            onAgentDisconnected={handleAgentDisconnected}
            // Изолированный звонок в историю чата не пишет (#1441): иначе
            // перезагрузка посреди звонка заменила бы переписку расшифровкой.
            sessionId={isolatedCall ? null : sessionId}
            workflowType={connectionDetails?.workflowType}
          />
        </LiveKitRoom>
      )}
    </>
  );

  // Портал в `document.body` нужен плавающему окну: внутри трансформированного
  // родителя `position: fixed` ломается в iOS Safari. Инлайновый чат
  // портируется в контейнер блока на странице — так виджет остаётся ОДНИМ
  // экземпляром на уровне `App` и переживает навигацию агента, вместо того
  // чтобы размонтироваться вместе со страницей.
  return createPortal(
    widgetTree,
    inline && inlineHost ? inlineHost : portalTarget
  );
};

/**
 * LiveKitSession - внутренний компонент для работы с LiveKit хуками
 */
const LiveKitSession = ({
  onSessionState,
  onMessagesChange,
  onControlsReady,
  onSendMessageReady,
  onUserSpeakingChange,
  onUserAudioLevelChange,
  onAgentAudioLevelChange,
  onAgentDisconnected,
  sessionId,
  workflowType,
}) => {
  const room = useRoomContext();
  const { state: agentState } = useLiveKitVoiceAssistant();
  const remoteParticipants = useRemoteParticipants();
  const {
    enabled: micEnabled,
    pending: micPending,
    toggle: toggleMicrophone,
  } = useTrackToggle({ source: Track.Source.Microphone });
  const { send: sendChatMessage } = useChat();
  const messages = useLiveKitTranscript();
  const previousSessionStateRef = useRef(null);
  const previousMessagesRef = useRef([]);
  const lastAppliedMetadataRef = useRef(null);
  const lastAppliedAttributesRef = useRef(null);
  const hadAgentRef = useRef(false); // Issue #593: guard — агент уже был в комнате

  // Issue #593: Track when agent has connected (to distinguish "not yet connected" from "left")
  useEffect(() => {
    if (remoteParticipants.length > 0) {
      hadAgentRef.current = true;
    }
  }, [remoteParticipants.length]);

  useEffect(() => {
    onControlsReady({
      toggleMute: async (enabled) => {
        await toggleMicrophone(enabled);
      },
      setMicEnabled: async (enabled) => {
        await room.localParticipant.setMicrophoneEnabled(enabled);
      },
      endCall: async () => {
        if (room.state !== 'disconnected') {
          await room.disconnect();
        }
      },
    });
    return () => {
      hadAgentRef.current = false; // Issue #593: Reset on unmount/new session
    };
  }, [toggleMicrophone, onControlsReady, room]);

  useEffect(() => {
    if (onSendMessageReady && sendChatMessage) {
      onSendMessageReady(sendChatMessage);
    }
  }, [onSendMessageReady, sendChatMessage]);

  useEffect(() => {
    const roomState = room.state;

    let status = 'disconnected';
    if (roomState === 'connected') {
      // Issue #593: Если агент БЫЛ и ушёл из комнаты — показываем disconnected, а не connecting
      // Guard hadAgentRef предотвращает false positive при начальном подключении
      if (hadAgentRef.current && remoteParticipants.length === 0) {
        status = 'disconnected';
      } else {
        const agentActive =
          agentState === 'speaking' ||
          agentState === 'listening' ||
          agentState === 'thinking';
        status = agentActive ? 'connected' : 'connecting';
      }
    } else if (roomState === 'connecting' || roomState === 'reconnecting') {
      status = 'connecting';
    }

    const nextState = {
      status,
      isSpeaking: agentState === 'speaking',
      isMuted: !micEnabled,
      micPending: micPending,
    };

    const prev = previousSessionStateRef.current;
    const shouldUpdate =
      !prev ||
      prev.status !== nextState.status ||
      prev.isSpeaking !== nextState.isSpeaking ||
      prev.isMuted !== nextState.isMuted ||
      prev.micPending !== nextState.micPending;

    if (shouldUpdate) {
      console.warn('[566-DEBUG] LiveKitSession state update:', {
        roomState: room.state,
        agentState,
        computedStatus: status,
        micEnabled,
        prevStatus: prev?.status,
      });
      previousSessionStateRef.current = nextState;
      onSessionState(nextState);
    }
  }, [
    room.state,
    agentState,
    micEnabled,
    micPending,
    remoteParticipants.length,
    onSessionState,
  ]);

  // Issue #593: Детекция ухода агента — уведомляем parent для graceful disconnect
  // Guard hadAgentRef предотвращает false positive при начальном подключении
  useEffect(() => {
    if (
      room.state === 'connected' &&
      hadAgentRef.current &&
      remoteParticipants.length === 0
    ) {
      console.warn('[LiveKitSession] Agent left the room, notifying parent');
      onAgentDisconnected?.();
    }
  }, [room.state, remoteParticipants.length, onAgentDisconnected]);

  useEffect(() => {
    const prev = previousMessagesRef.current;
    const sameLength = prev.length === messages.length;
    const allEqual =
      sameLength &&
      prev.every((msg, index) => {
        const next = messages[index];
        return (
          msg.id === next.id &&
          msg.role === next.role &&
          msg.text === next.text &&
          msg.timestamp?.getTime?.() === next.timestamp?.getTime?.()
        );
      });

    if (!allEqual) {
      previousMessagesRef.current = messages;
      onMessagesChange(messages);

      if (sessionId && messages.length > 0) {
        saveMessages(sessionId, messages);
      }
    }
  }, [messages, onMessagesChange, sessionId]);

  useEffect(() => {
    if (!sessionId || room.state !== 'connected') {
      return;
    }

    const attrSessionId = lastAppliedAttributesRef.current;
    const metaSessionId = lastAppliedMetadataRef.current;

    if (attrSessionId === sessionId && metaSessionId === sessionId) {
      return;
    }

    const applyMetadata = async () => {
      try {
        await Promise.all([
          room.localParticipant.setMetadata(
            JSON.stringify({
              sessionId,
              inventoryId: config.INVENTORY_ID,
              workflowType: workflowType || config.WORKFLOW_TYPE,
            })
          ),
          room.localParticipant.setAttributes({
            sessionId,
            inventoryId: config.INVENTORY_ID,
            workflowType: workflowType || config.WORKFLOW_TYPE,
          }),
        ]);
        lastAppliedMetadataRef.current = sessionId;
        lastAppliedAttributesRef.current = sessionId;
      } catch (error) {
        console.error('[LiveKitSession] Failed to set metadata:', error);
      }
    };

    applyMetadata();
  }, [room, room.state, sessionId, workflowType]);

  // FIXED: Combined audio level detection for both user and agent with throttling
  useEffect(() => {
    if (
      !room ||
      !onUserSpeakingChange ||
      !onUserAudioLevelChange ||
      !onAgentAudioLevelChange
    )
      return;

    const AUDIO_LEVEL_THRESHOLD = 0.01; // Threshold for detecting speech
    const CHECK_INTERVAL = 150; // OPTIMIZED: Reduced frequency to 150ms (was 100ms)
    const THROTTLE_THRESHOLD = 0.05; // Only update if change is > 5%

    let prevUserLevel = 0;
    let prevAgentLevel = 0;

    const intervalId = setInterval(() => {
      try {
        // Get user audio level
        const userLevel = room.localParticipant.audioLevel;
        const isUserSpeaking = userLevel > AUDIO_LEVEL_THRESHOLD;

        // Get agent audio level from remote participants
        const remoteParticipants = Array.from(room.remoteParticipants.values());
        const agentLevel =
          remoteParticipants.length > 0
            ? remoteParticipants[0].audioLevel || 0
            : 0;

        // FIXED: Only update if significant change (throttling to reduce re-renders)
        const userLevelChanged =
          Math.abs(userLevel - prevUserLevel) >= THROTTLE_THRESHOLD;
        const agentLevelChanged =
          Math.abs(agentLevel - prevAgentLevel) >= THROTTLE_THRESHOLD;

        if (
          userLevelChanged ||
          isUserSpeaking !== prevUserLevel > AUDIO_LEVEL_THRESHOLD
        ) {
          onUserSpeakingChange(isUserSpeaking);
          onUserAudioLevelChange(userLevel);
          prevUserLevel = userLevel;
        }

        if (agentLevelChanged) {
          onAgentAudioLevelChange(agentLevel);
          prevAgentLevel = agentLevel;
        }
      } catch (error) {
        // FIXED: Log errors in development for debugging
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '[Audio Level Detection] Failed to get audio levels:',
            error
          );
        }
        // Fallback to 0 on error
        onUserAudioLevelChange?.(0);
        onAgentAudioLevelChange?.(0);
      }
    }, CHECK_INTERVAL);

    return () => clearInterval(intervalId);
  }, [
    room,
    onUserSpeakingChange,
    onUserAudioLevelChange,
    onAgentAudioLevelChange,
  ]);

  return null;
};

export default VoiceWidgetConnected;
